import { useEffect, useState, useRef, useCallback } from 'react'
import { clsx } from 'clsx'
import {
  TrendingUp, TrendingDown, Minus, Bot, Target, Shield, Activity,
  RefreshCw, ArrowRight, AlertCircle, AlertTriangle, BarChart2, Zap, Settings, Eye, EyeOff, ChevronDown, Percent, FileText,
  X, Clock, CalendarDays, Radar,
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
  LineChart, Line,
} from 'recharts'
import { useAgents } from '@/hooks/useAgents'
import { usePortfolio } from '@/hooks/usePortfolio'
import { useRealtimePortfolio } from '@/hooks/useRealtime'
import { useAccounts } from '@/hooks/useAccounts'
import { Link, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import SmartAdvisorPanel from '@/components/ui/SmartAdvisorPanel'
import { DeployCashHero } from '@/routes/DeployCashPage'
import { cleanMarketSummary } from '@/routes/PredictionMarketsPage'
import ActivityFeed from '@/components/ui/ActivityFeed'
import LivePnlCard from '@/components/ui/LivePnlCard'
import InsightsCard from '@/components/ui/InsightsCard'
import CriticalAlertsCard from '@/components/ui/CriticalAlertsCard'
import DataSection from '@/components/ui/DataSection'
import { pushAnalysisNotifications } from '@/hooks/useNotifications'
import { getStrategyRecommendations, getPortfolioHealth, getTopMovers, getUpcomingDividends, getInsights, dismissInsight, api } from '@/lib/api'
import type { TopMoverEntry, UpcomingDividend, Insight } from '@/lib/api'
import { useRecentActivity } from '@/hooks/useRecentActivity'
import type { StrategyRecommendation, PortfolioHealthResponse, PortfolioFit } from '@/lib/types'
import { PortfolioFitBadge } from '@/components/ui/PortfolioFitBadge'

import { fmtGBP, fmtNum, fmtPct, fmtPctSigned, fmtSigned, fmtAgo } from '@/lib/format'
import { navigateToTerminalCommand } from '@/lib/navigation'
import { fmtSetupPrice } from './TradeIdeasPage'

// Plain-English expansions for financial jargon (shown as title tooltips on
// widget headings and stat labels — same pattern as WRAPPER_HELP on the
// Deploy Cash page). This is a pro surface; the terms stay, but every one
// carries a one-sentence explanation a novice can hover for.
const JARGON_HELP: Record<string, string> = {
  sharpe: 'Sharpe ratio — return earned per unit of risk taken; above 1 is good, above 2 is excellent',
  drawdown: 'Drawdown — how far the portfolio has fallen from its most recent peak; "max drawdown" is the worst such fall',
  kelly: 'Kelly — a formula for how much to stake given your edge; the Kelly scalar shrinks all position sizes when market conditions worsen',
  var: 'VaR (Value at Risk) — the most you would expect to lose on a normal bad day; at 95%, only 1 day in 20 should be worse than this',
  regime: 'Regime — the market’s overall "weather" (risk-on vs risk-off), read from volatility, interest rates, breadth and the dollar; strategies that work in one regime often fail in another',
  alpha: 'Alpha — return above what the market itself delivered; the part of performance down to skill or an information edge rather than just being invested',
  beta: 'Beta — how much a holding moves when the whole market moves; 1 = in line with the market, 2 = twice as hard in both directions',
  expectancy: 'Expectancy — the average £ made (or lost) per trade once all wins and losses are netted; positive expectancy means the approach makes money over many trades',
  confluence: 'Confluence — several independent sources or signal types pointing the same way at the same time; more agreement = stronger evidence than any single signal',
  breadth: 'Breadth — how many stocks are joining a market move; a rally with narrow breadth (a few big names doing all the work) is fragile',
  sigma: 'Sigma / z-score — how unusual a move is compared with that asset’s own history; 2+ means genuinely rare, not just everyday noise',
  bps: 'bps (basis points) — hundredths of a percent; 50 bps = 0.50%',
  rMultiple: 'R-multiple — profit or loss measured in units of the £ risked at the stop; +2R means the trade made twice what it risked',
  rr: 'Risk/Reward — the distance from entry to target divided by the distance from entry to stop; 2 means the upside is twice the downside being risked',
  winRate: 'Win rate — the share of outcomes that finished positive; a high win rate can still lose money overall if the losers are much bigger than the winners',
  exBest: 'Ex-best — the same statistics recalculated with the single best trade removed; shows whether the results depend on one lucky outlier',
  cagr: 'CAGR — the smoothed yearly growth rate: what constant annual return would have produced the same overall result',
  volatility: 'Volatility — how violently a price swings around, in either direction; higher volatility means wider daily moves and bigger surprises',
  correlation: 'Correlation — how much two things move together; +1 = in lockstep, 0 = unrelated, −1 = opposite directions',
  momentum: 'Momentum — the tendency of recent winners to keep winning in the near term; here, the strength and persistence of the current move',
  rsi: 'RSI (Relative Strength Index) — a 0–100 gauge of recent buying vs selling pressure; above 70 is often "overbought", below 30 "oversold"',
  vwap: 'VWAP — today’s average traded price weighted by volume; price above VWAP means buyers have been paying up all session',
  conviction: 'Conviction — a 0–100 confidence score built from how many independent sources agree and how strongly they agree',
  sentiment: 'Sentiment — the bullish/bearish lean of the news and signal flow, scored from −1 (very bearish) to +1 (very bullish)',
  blotter: 'Blotter — the trading diary: every idea you saved or acted on, with entry, stop and how it has gone since',
  dispersion: 'Dispersion — how far apart the best and worst names in a sector are performing; wide dispersion favours stock-picking, narrow favours just holding the index',
  coherence: 'Coherence — whether acting on today’s signals would over-concentrate your portfolio in a sector it is already heavy in',
  heat: 'Heat — an attention score (sources × signals × strength of sentiment): the tickers the whole signal pipeline is loudest about right now',
  stop: 'Stop — the pre-agreed exit price that caps the loss if the trade goes the wrong way',
  target: 'Target — the price at which the plan says take profit',
  size: 'Size — how much money the engine suggests committing to this trade, scaled by conviction and the current market regime',
  pnl: 'P&L (profit and loss) — how much money was made or lost over the period, in £',
  entry: 'Entry — the price at which the plan says open the trade',
  atr: 'ATR (Average True Range) — how far the price typically travels in a day; stops are set wide enough to survive this normal noise',
  golden: 'Golden setup — an idea confirmed by 3+ independent sources with the market regime pointing the same way',
  multiCat: 'Multi-category convergence — several different KINDS of evidence (technical, regulatory, sentiment, macro, options…) agreeing on one ticker; much rarer and stronger than several feeds of the same kind',
  perfectStorm: 'Perfect storm — every quality filter firing at once on a saved idea: several signal categories agree, the market regime supports the direction, and the price is at the planned entry',
  catalyst: 'Catalyst — a dated event (earnings, ruling, product launch) likely to move the price when it lands',
  smartMoney: 'Smart money — trades by insiders, big funds and politicians, who may know something the market does not yet',
  book: 'Book — the set of positions you currently hold',
  discretionary: 'Discretionary — money you are free to trade yourself; non-discretionary accounts are adviser-managed and shown for information only',
  wrapper: 'Wrapper — the tax container an account lives in (ISA, SIPP, GIA); the same investment is taxed differently depending on its wrapper',
  exDividend: 'Ex-dividend date — you must own the share before this date to receive its next dividend payment',
  healthScore: 'Health score — a 0–100 rules-based check of diversification, cash drag and risk; the letter is the overall grade',
  priority: 'Priority — P1 is most urgent, P5 least; ranked by estimated £ impact',
  phdSwarm: 'PhD Swarm — a panel of AI analysts that each research a different angle of the same question, then vote',
  signalQuality: 'Signal quality — which feeds have actually predicted moves: hit rate and average edge per source',
}

function getMarketSession(): { label: string; color: string } {
  const now = new Date()
  const nyHour = Number(
    new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: 'America/New_York' }).format(now)
  )
  const nyMin = Number(
    new Intl.DateTimeFormat('en-US', { minute: 'numeric', timeZone: 'America/New_York' }).format(now)
  )
  const day = new Date(
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York' }).format(now)
  ).getDay()
  const totalMin = nyHour * 60 + nyMin

  if (day === 0 || day === 6) return { label: 'MARKET CLOSED', color: '#f43f5e' }
  if (totalMin < 4 * 60) return { label: 'AFTER HOURS', color: '#f59e0b' }
  if (totalMin < 9 * 60 + 30) return { label: 'PRE-MARKET', color: '#f59e0b' }
  if (totalMin < 16 * 60) return { label: 'MARKET OPEN', color: '#10b981' }
  if (totalMin < 20 * 60) return { label: 'AFTER HOURS', color: '#f59e0b' }
  return { label: 'MARKET CLOSED', color: '#f43f5e' }
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  const pct = Math.round(value * 100)
  const color = pct > 65 ? '#f43f5e' : pct > 35 ? '#f59e0b' : '#10b981'
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-text-secondary">
        <span>{label}</span><span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-surface-hover rounded-full overflow-hidden" role="presentation">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}

/** Placeholder shown inside a card when data is unavailable. */
function EmptyState({ icon, message, sub }: { icon?: React.ReactNode; message: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
      {icon && <div className="text-text-muted opacity-40">{icon}</div>}
      <p className="text-sm text-text-muted">{message}</p>
      {sub && <p className="text-xs text-text-muted opacity-60">{sub}</p>}
    </div>
  )
}

/** Simple bar chart of account values when no time-series history exists. */
function AccountSnapshot({ accounts }: { accounts: { name: string; value: number }[] }) {
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f43f5e', '#06b6d4', '#e879f9', '#a3e635']
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={accounts} margin={{ top: 4, right: 0, bottom: 24, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          interval={0}
          angle={-35}
          textAnchor="end"
        />
        <YAxis
          tickFormatter={v => `£${(v / 1e6).toFixed(2)}M`}
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={72}
        />
        <Tooltip
          formatter={(v: number) => [fmtGBP(v), 'Value']}
          contentStyle={{ background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', borderRadius: 8, color: 'rgb(var(--text-primary))' }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {accounts.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

// ── Action Plan Panel: top-3 personalised strategy recommendations ──────────

const ACTION_BADGE: Record<string, string> = {
  SELL:   'badge-red',
  BUY:    'badge-green',
  REVIEW: 'badge-amber',
}

const PRIORITY_BADGE: Record<number, string> = {
  1: 'badge-red',
  2: 'badge bg-orange-500/10 text-orange-400 border border-orange-500/20',
  3: 'badge-amber',
  4: 'badge-blue',
  5: 'badge',
}

interface MorningBookCatalyst {
  ticker: string
  category: string
  summary: string
}

function MorningNarrativeBanner() {
  const [narrative, setNarrative] = useState<{ narrative: string; method: string } | null>(null)
  const [bookCatalysts, setBookCatalysts] = useState<MorningBookCatalyst[]>([])
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/winners/morning-narrative')
        if (cancelled) return
        if (res.data?.narrative) {
          setNarrative({ narrative: res.data.narrative, method: res.data.method })
        }
        const bc = res.data?.facts?.book_catalysts ?? []
        if (Array.isArray(bc)) setBookCatalysts(bc.slice(0, 4))
      } catch { /* silent */ }
    })()
    return () => { cancelled = true }
  }, [])

  if (dismissed || !narrative?.narrative) return null
  const isLlm = narrative.method === 'llm'
  return (
    <div className={`rounded-xl border p-4 ${isLlm ? 'bg-gradient-to-br from-blue-500/[0.06] to-amber-500/[0.04] border-blue-500/30' : 'bg-surface-card border-border'}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="panel-title">Morning Brief</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded border cursor-help ${isLlm ? 'bg-blue-500/20 text-blue-400 border-blue-500/40' : 'bg-surface-hover text-text-muted border-border'}`}
              title={isLlm ? 'This brief was written by the DeepSeek AI model from today’s live data' : 'This brief was assembled from a rules-based template (the AI writer was unavailable)'}
            >
              {isLlm ? 'DeepSeek' : 'template'}
            </span>
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">{narrative.narrative}</p>
          {bookCatalysts.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-3 pt-2 border-t border-border/30">
              <span className="text-[11px] uppercase text-blue-400 font-semibold tracking-wider">On your book:</span>
              {bookCatalysts.map((c, i) => (
                <Link
                  key={`${c.ticker}-${i}`}
                  to={`/app/stock/${encodeURIComponent(c.ticker)}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20 transition"
                  title={c.summary}
                >
                  <span className="font-bold">{c.ticker}</span>
                  <span className="opacity-70">{c.category}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-text-muted hover:text-text-primary text-xs shrink-0"
          aria-label="Dismiss morning brief"
          title="Hide for this session"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

interface RegimeComponent { score: number; label: string; detail: string }
interface MacroRegime {
  score: number
  label: string
  components: {
    vix_term_structure: RegimeComponent
    yield_curve: RegimeComponent
    sector_breadth: RegimeComponent
    dollar_index: RegimeComponent
  }
}

type RegimeHistoryPoint = { t: string; score: number; label: string; band: string }
type RegimeTransition = { t: string; from_band: string; to_band: string; delta: number }

type RegimeMomentum = { delta_24h: number; direction: 'improving' | 'deteriorating' | 'flat' }

// Plain-English read of the regime score — the sentence leads; numbers live
// behind "Show the detail". Bands mirror the score colour bands below.
function regimeSentence(score: number): string {
  if (score >= 2.5) return 'Markets look very calm — a good day to invest as planned.'
  if (score >= 1.0) return 'Markets look calm — a normal day to invest.'
  if (score >= -1.0) return 'Markets are mixed — nothing unusual, stick to your plan.'
  if (score >= -2.5) return 'Markets are jittery — a day for patience, not big moves.'
  return 'Markets are stressed — sit tight and avoid big moves today.'
}

const REGIME_DETAIL_KEY = 'av-regime-detail-open'

function MacroRegimeBadge() {
  const [data, setData] = useState<MacroRegime | null>(null)
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(REGIME_DETAIL_KEY) === '1' } catch { return false }
  })
  const [history, setHistory] = useState<RegimeHistoryPoint[] | null>(null)
  const [transitions, setTransitions] = useState<RegimeTransition[]>([])
  const [momentum, setMomentum] = useState<RegimeMomentum | null>(null)

  useEffect(() => {
    let cancelled = false
    const loadAll = async () => {
      try {
        const [r, m] = await Promise.allSettled([
          api.get('/api/macro/regime'),
          api.get('/api/macro/regime/momentum'),
        ])
        if (!cancelled) {
          if (r.status === 'fulfilled') setData(r.value.data)
          if (m.status === 'fulfilled' && m.value.data?.delta_24h !== undefined) {
            setMomentum(m.value.data)
          }
        }
      } catch { /* silent */ }
    }
    loadAll()
    const id = setInterval(loadAll, 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  useEffect(() => {
    if (!expanded || history !== null) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/macro/regime/history?hours=168')
        if (cancelled) return
        setHistory(res.data?.snapshots ?? [])
        setTransitions(res.data?.transitions ?? [])
      } catch {
        if (!cancelled) { setHistory([]); setTransitions([]) }
      }
    })()
    return () => { cancelled = true }
  }, [expanded, history])

  if (!data) return null

  // Higher-contrast tints — the previous /10 + text-X-300 read as
  // dim brown/rust on dark theme. Score numbers need to pop, not blend.
  const colour =
    data.score >= 2.5 ? 'bg-emerald-500/25 text-emerald-100 border-emerald-500/60'
    : data.score >= 1.0 ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/50'
    : data.score >= -1.0 ? 'bg-surface-hover text-text-primary border-border-light'
    : data.score >= -2.5 ? 'bg-rose-500/20 text-rose-200 border-rose-500/50'
    : 'bg-rose-500/30 text-rose-100 border-rose-500/70'

  // The score number must read bright regardless of what colour it would
  // otherwise *inherit*. A class applied directly to the span always wins
  // over an inherited value, so set it explicitly here — this is what stops
  // the number rendering near-black on the neutral band.
  const scoreColour =
    data.score >= 1.0 ? 'text-emerald-400'
    : data.score <= -1.0 ? 'text-rose-400'
    : 'text-text-primary'

  const toggleDetail = () => {
    setExpanded(v => {
      const next = !v
      try { localStorage.setItem(REGIME_DETAIL_KEY, next ? '1' : '0') } catch { /* ignore */ }
      return next
    })
  }

  return (
    <div className={`rounded-xl border px-4 py-2 ${colour}`}>
      {/* Plain sentence first — the numbers live behind "Show the detail" */}
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm font-semibold flex-1 min-w-0">
          {regimeSentence(data.score)}
        </p>
        <button
          type="button"
          onClick={toggleDetail}
          className="text-[11px] underline opacity-70 hover:opacity-100 shrink-0"
          aria-expanded={expanded}
        >
          {expanded ? 'Hide the detail ▲' : 'Show the detail ▼'}
        </button>
      </div>
      {expanded && (
        <>
          <div className="flex items-center gap-3 flex-wrap mt-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold opacity-70 cursor-help" title={JARGON_HELP.regime}>
              Macro Regime
            </span>
            <span className={`text-base font-bold font-mono ${scoreColour}`}>
              {fmtSigned(data.score)}
            </span>
            <span className="text-sm font-semibold">{data.label}</span>
            {momentum && (
              <span
                className={
                  momentum.direction === 'improving' ? 'text-[11px] text-emerald-400'
                  : momentum.direction === 'deteriorating' ? 'text-[11px] text-rose-400'
                  : 'text-[11px] opacity-60'
                }
                title="24h delta vs moving average"
              >
                {momentum.direction === 'improving' ? '↗' : momentum.direction === 'deteriorating' ? '↘' : '→'}
                {' '}
                24h {fmtSigned(momentum.delta_24h)}
              </span>
            )}
            <Link
              to="/app/regime"
              className="text-[11px] underline opacity-60 hover:opacity-100"
              title="Open full regime dashboard"
            >
              full view
            </Link>
            <span
              className="text-[11px] opacity-70 ml-auto cursor-help"
              title={`VIX = the market's expected-volatility "fear" gauge · Curve = yield-curve slope (inverted often precedes recessions) · ${JARGON_HELP.breadth} · DXY = US dollar index`}
            >
              VIX: {data.components.vix_term_structure.label}
              {' · '}Curve: {data.components.yield_curve.label}
              {' · '}Breadth: {data.components.sector_breadth.label}
              {' · '}DXY: {data.components.dollar_index.label}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3 text-[11px]">
            {Object.entries(data.components).map(([key, c]) => (
              <div key={key} className="bg-black/20 rounded p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="opacity-60 uppercase tracking-wider text-[10px]">
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span className="font-mono font-bold">
                    {fmtSigned(c.score, 1)}
                  </span>
                </div>
                <div className="font-semibold mb-0.5">{c.label}</div>
                <div className="opacity-70 text-[11px] leading-tight">{c.detail}</div>
              </div>
            ))}
          </div>
          <RegimeHistorySpark history={history} transitions={transitions} />
        </>
      )}
    </div>
  )
}

function RegimeHistorySpark({
  history, transitions,
}: { history: RegimeHistoryPoint[] | null; transitions: RegimeTransition[] }) {
  if (history === null) {
    return <div className="mt-3 text-[11px] opacity-50">Loading 7d regime history…</div>
  }
  if (history.length < 2) {
    return (
      <div className="mt-3 text-[11px] opacity-50">
        Regime history accumulates hourly — not enough snapshots yet.
      </div>
    )
  }

  const w = 600, h = 56, pad = 4
  const scores = history.map(p => p.score)
  const minS = Math.min(-2.5, ...scores)
  const maxS = Math.max(2.5, ...scores)
  const xOf = (i: number) => pad + (i / (history.length - 1)) * (w - 2 * pad)
  const yOf = (s: number) => pad + (1 - (s - minS) / (maxS - minS)) * (h - 2 * pad)
  const zeroY = yOf(0)
  const d = history.map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(p.score).toFixed(1)}`).join(' ')

  const last = history[history.length - 1]
  const lastColor =
    last.score >= 2.5 ? '#10b981'
    : last.score >= 1.0 ? '#34d399'
    : last.score >= -1.0 ? '#a1a1aa'
    : last.score >= -2.5 ? '#f43f5e'
    : '#dc2626'

  const tMap = new Map(transitions.map(t => [t.t, t]))
  const recent = transitions.slice(-3).reverse()

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <span className="opacity-60 uppercase tracking-wider text-[11px] cursor-help" title={JARGON_HELP.regime}>
          7-day regime trajectory
        </span>
        <span className="opacity-50 text-[10px]">
          {history.length} snapshots · {transitions.length} transitions
        </span>
      </div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} className="block bg-black/20 rounded" preserveAspectRatio="none">
        <line x1={pad} y1={yOf(1)} x2={w - pad} y2={yOf(1)} stroke="#10b98130" strokeDasharray="2 3" />
        <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="#71717a40" strokeDasharray="2 3" />
        <line x1={pad} y1={yOf(-1)} x2={w - pad} y2={yOf(-1)} stroke="#f43f5e30" strokeDasharray="2 3" />
        <path d={d} fill="none" stroke={lastColor} strokeWidth={1.5} />
        {history.map((p, i) => {
          if (!tMap.has(p.t)) return null
          return (
            <circle
              key={p.t}
              cx={xOf(i)}
              cy={yOf(p.score)}
              r={2.5}
              fill="#fbbf24"
              stroke="#000"
              strokeWidth={0.5}
            >
              <title>{`${new Date(p.t).toLocaleString()} — band change`}</title>
            </circle>
          )
        })}
        <circle cx={xOf(history.length - 1)} cy={yOf(last.score)} r={2.5} fill={lastColor} />
      </svg>
      {recent.length > 0 && (
        <div className="mt-2 space-y-0.5 text-[11px] opacity-80">
          {recent.map(tr => (
            <div key={tr.t} className="flex items-center gap-2">
              <span className="opacity-50 font-mono">
                {new Date(tr.t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {' '}
                {new Date(tr.t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span>
                <span className="opacity-60">{tr.from_band}</span>
                <span className="mx-1">→</span>
                <span className="font-semibold">{tr.to_band}</span>
              </span>
              <span className="opacity-60 font-mono">
                Δ {fmtSigned(tr.delta)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

type DashSetup = {
  ticker: string
  direction: 'LONG' | 'SHORT' | 'WATCH'
  /** Portfolio-resolved verb: LONG becomes HOLD/TRIM when the position is
   *  already full; equals direction otherwise. Optional — old payloads
   *  without it must render exactly as before. */
  resolved_action?: string
  conviction: number
  avg_sentiment: number
  source_count: number
  sources?: string[]
  setup?: {
    entry: number
    stop: number
    target: number
    rr_ratio: number
    atr_pct: number
  } | null
  position_sizing?: {
    suggested_gbp?: number
    /** Original pre-constraint size, preserved when suggested_gbp is zeroed
     *  for HOLD/TRIM-resolved ideas. */
    suggested_gbp_unconstrained?: number
    regime_score?: number
    regime_label?: string
  }
  is_megacap?: boolean
  already_held?: boolean
  tradeable?: boolean
  portfolio_fit?: PortfolioFit | null
  position_note?: string | null
}

function SetupOfTheDay() {
  const [top, setTop] = useState<DashSetup | null>(null)
  const [loading, setLoading] = useState(true)
  const [kept, setKept] = useState(false)
  const [keepBusy, setKeepBusy] = useState(false)
  const [keepError, setKeepError] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get('/api/signals/trade-ideas?regime_aligned_only=true')
        const ideas: DashSetup[] = res.data?.ideas ?? []
        // Pick the highest-conviction ACTIONABLE idea. Guard against nonsense:
        //  - must be tradeable
        //  - a SHORT/SELL only makes sense on a ticker you actually HOLD
        //    (you can't "sell" PLTR if you own none) — so exclude unheld shorts
        //  - for LONG/WATCH, prefer ideas you don't already hold (fresh exposure)
        const ranked = ideas
          .filter(i => {
            if (i.tradeable === false) return false
            const dir = i.direction
            if (dir === 'SHORT') return i.already_held === true   // only trim/short what you own
            return !i.already_held                                 // fresh LONG/WATCH
          })
          .sort((a, b) => (b.conviction ?? 0) - (a.conviction ?? 0))
        if (!cancelled) {
          // No silent fallback to an unfiltered idea — that's how a "SELL <unheld>"
          // slipped through. If nothing qualifies, show the empty state.
          setTop(ranked[0] ?? null)
          setKept(false)   // new top idea resets kept state
          setKeepError(false)
        }
      } catch {
        if (!cancelled) setTop(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const iv = setInterval(load, 120_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  const handleKeep = async () => {
    if (!top || kept || keepBusy) return
    setKeepBusy(true)
    try {
      await api.post('/api/blotter/ideas', {
        ticker: top.ticker,
        direction: top.direction,
        conviction: top.conviction,
        entry_price: top.setup?.entry,
        stop_price: top.setup?.stop,
        target_price: top.setup?.target,
        rr_ratio: top.setup?.rr_ratio,
        atr_pct: top.setup?.atr_pct,
        suggested_gbp: top.position_sizing?.suggested_gbp,
        regime_score: top.position_sizing?.regime_score,
        regime_label: top.position_sizing?.regime_label,
        sources: top.sources ?? [],
        source_count: top.source_count,
        avg_sentiment: top.avg_sentiment,
        rationale: `Setup of the Day — conviction ${Math.round(top.conviction ?? 0)}%`,
      })
      setKept(true)
      setKeepError(false)
    } catch {
      // Surface the failure inline — a silently-swallowed save leaves the user
      // believing the idea is in the blotter when it isn't.
      setKeepError(true)
    } finally {
      setKeepBusy(false)
    }
  }

  if (loading || !top) return null

  // BULLETPROOF GATE: never render a SELL/SHORT for a ticker not clearly held —
  // case-insensitive, and treats missing already_held as "not held". Last line
  // of defence behind the selection filter so a data quirk (lowercase direction,
  // undefined already_held) can't surface "SELL <unheld>".
  const _dir = String(top.direction ?? '').toUpperCase()
  if ((_dir === 'SHORT' || _dir === 'SELL') && top.already_held !== true) return null

  // Portfolio-resolved action drives verb + colour. Fail-soft: old payloads
  // without resolved_action fall back to direction and render as before.
  const action = String(top.resolved_action ?? top.direction ?? '').toUpperCase()

  const tone =
    action === 'LONG' ? 'from-emerald-500/10 to-emerald-500/0 border-emerald-500/40'
    : action === 'SHORT' ? 'from-rose-500/10 to-rose-500/0 border-rose-500/40'
    : action === 'HOLD' ? 'from-sky-500/10 to-sky-500/0 border-sky-500/40'
    : action === 'TRIM' ? 'from-amber-500/10 to-amber-500/0 border-amber-500/40'
    : 'from-surface-hover/60 to-transparent border-border'

  const actionLabel =
    action === 'LONG' ? 'BUY'
    : action === 'SHORT' ? 'SELL'
    : action === 'HOLD' ? 'HOLD — don’t add'
    : action === 'TRIM' ? 'TRIM — reduce, don’t buy'
    : 'WATCH'
  const actionTone =
    action === 'LONG' ? 'text-emerald-400'
    : action === 'SHORT' ? 'text-rose-400'
    : action === 'HOLD' ? 'text-sky-400'
    : action === 'TRIM' ? 'text-amber-400'
    : 'text-text-secondary'

  // No new money when the backend zeroed the size for a HOLD/TRIM resolution.
  const noNewMoney = (action === 'HOLD' || action === 'TRIM')
    && (top.position_sizing?.suggested_gbp ?? 0) <= 0

  // Plain-English "why" — so the call never looks arbitrary.
  const sentWord = (top.avg_sentiment ?? 0) > 0.1 ? 'bullish' : (top.avg_sentiment ?? 0) < -0.1 ? 'bearish' : 'mixed'
  const dirVerb =
    action === 'LONG' ? 'buy'
    : action === 'SHORT' ? 'trim/short'
    : action === 'HOLD' ? 'hold (not add to)'
    : action === 'TRIM' ? 'trim'
    : 'watch'
  const rationale =
    `${top.source_count ?? 0} independent source${(top.source_count ?? 0) === 1 ? '' : 's'} agree, ${sentWord} signal` +
    (top.position_sizing?.regime_label ? ` · regime: ${top.position_sizing.regime_label}` : '') +
    (top.already_held ? ' · you hold this' : '') +
    `. Reason to ${dirVerb} ${top.ticker}.`

  return (
    <div className={`rounded-xl border bg-gradient-to-r ${tone} p-4`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">
            Setup of the Day
          </span>
          <span className={`text-2xl font-bold font-mono ${actionTone}`}>
            {actionLabel}
          </span>
          <Link
            to={`/app/stock/${encodeURIComponent(top.ticker)}`}
            className="text-2xl font-bold text-text-primary hover:text-blue-light font-mono"
          >
            {top.ticker}
          </Link>
          <PortfolioFitBadge fit={top.portfolio_fit} compact title={top.position_note ?? undefined} />
          {top.setup && (
            <span className="text-text-secondary cursor-help" title={JARGON_HELP.entry}>
              @ <span className="font-mono font-semibold text-text-primary">{fmtSetupPrice(top.setup.entry, top.ticker)}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <div className="flex flex-col max-w-[260px]">
            <span className="text-text-muted">Why</span>
            <span className="text-text-secondary leading-snug">{rationale}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-text-muted cursor-help" title={JARGON_HELP.conviction}>Conviction</span>
            <span className="font-mono font-bold text-text-primary">{Math.min(100, Math.round(top.conviction ?? 0))}%</span>
          </div>
          {top.setup && (
            <>
              <div className="flex flex-col">
                <span className="text-rose-400 cursor-help" title={JARGON_HELP.stop}>Stop</span>
                <span className="font-mono text-rose-400">{fmtSetupPrice(top.setup.stop, top.ticker)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-emerald-400 cursor-help" title={JARGON_HELP.target}>Target</span>
                <span className="font-mono text-emerald-400">{fmtSetupPrice(top.setup.target, top.ticker)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-text-muted cursor-help" title={JARGON_HELP.rr}>R/R</span>
                <span className="font-mono text-text-primary">{fmtNum(top.setup.rr_ratio, 1)}</span>
              </div>
            </>
          )}
          {top.position_sizing?.suggested_gbp !== undefined && (
            <div className="flex flex-col">
              <span className="text-text-muted cursor-help" title={JARGON_HELP.size}>Size</span>
              {noNewMoney ? (
                <span className="text-text-muted leading-tight" title="The position is already full — no new money.">
                  no new money
                </span>
              ) : (
                <span className="font-mono text-text-primary">
                  £{Math.round(top.position_sizing.suggested_gbp).toLocaleString('en-GB')}
                </span>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleKeep}
            disabled={kept || keepBusy}
            className={
              kept
                ? 'flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                : 'flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20 transition'
            }
            title={kept ? 'Kept in blotter' : 'Keep this setup in the blotter'}
          >
            {kept ? '✓ KEPT' : '★ KEEP'}
          </button>
          {keepError && !kept && (
            <button
              type="button"
              onClick={handleKeep}
              disabled={keepBusy}
              className="text-[11px] text-rose-400 underline hover:text-rose-300"
              title="Saving to the blotter failed — click to retry"
            >
              couldn’t save — retry
            </button>
          )}
          <Link
            to="/app/trade-ideas"
            className="text-[11px] underline text-text-muted hover:text-text-primary"
          >
            all ideas →
          </Link>
        </div>
      </div>
      {top.sources && top.sources.length > 0 && (
        <div className="mt-2 text-[11px] text-text-muted flex items-center gap-1.5 flex-wrap">
          <span className="opacity-70">Evidence:</span>
          {top.sources.slice(0, 6).map(s => (
            <span key={s} className="px-1.5 py-0.5 rounded bg-surface-card/60 text-text-secondary">
              {s.replace(/_/g, ' ')}
            </span>
          ))}
          {top.sources.length > 6 && (
            <span className="text-text-muted">+{top.sources.length - 6} more</span>
          )}
        </div>
      )}
    </div>
  )
}

type LiveSignal = {
  id?: string
  source: string
  signal_type: string
  ticker: string | null
  sentiment: number
  confidence: number
  summary: string
  /** Backend feed rows carry `timestamp` (ISO string of the DB created_at). */
  timestamp: string
}

function LiveSignalsFeed() {
  const [signals, setSignals] = useState<LiveSignal[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get('/api/signals/feed?limit=12&max_age_hours=6')
        const raw: LiveSignal[] = res.data?.signals ?? []
        // Only show ticker-specific signals, skip macro-only ones
        const tickerOnly = raw.filter(s => s.ticker && s.ticker.length > 0).slice(0, 10)
        if (!cancelled) setSignals(tickerOnly)
      } catch {
        if (!cancelled) setSignals([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const iv = setInterval(load, 90_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  if (loading) return null
  if (signals.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-blue-400" />
          <h2 className="panel-title">Live Signals</h2>
          <span className="text-[11px] text-text-muted">Last 6h · {signals.length} shown</span>
        </div>
        <Link to="/app/signals" className="text-[11px] text-text-muted hover:text-text-primary underline">
          all signals →
        </Link>
      </div>
      <div className="space-y-1.5">
        {signals.map((s, i) => {
          const sent = typeof s.sentiment === 'number' && Number.isFinite(s.sentiment) ? s.sentiment : 0
          const colour =
            sent >= 0.3 ? 'text-emerald-400'
            : sent <= -0.3 ? 'text-rose-400'
            : 'text-text-muted'
          return (
            <div
              key={s.id ?? `${s.source}-${s.ticker}-${s.timestamp}-${i}`}
              className="flex items-center gap-2 text-xs border-b border-border/30 pb-1.5 last:border-b-0"
            >
              <Link
                to={`/app/stock/${encodeURIComponent(s.ticker!)}`}
                className="font-mono font-bold text-blue-light hover:underline w-16 shrink-0"
              >
                {s.ticker}
              </Link>
              <span className={`font-mono font-semibold w-12 text-right ${colour}`}>
                {sent >= 0 ? '+' : ''}{sent.toFixed(2)}
              </span>
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-card/60 text-text-muted shrink-0 truncate max-w-[120px]">
                {s.source.replace(/_/g, ' ')}
              </span>
              <span className="text-text-secondary truncate flex-1">{s.summary}</span>
              <span className="text-[11px] text-text-muted shrink-0">{fmtAgo(s.timestamp)}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

type BookEntry = {
  ticker: string
  name: string
  value_gbp: number
  accounts: string[]
  discretionary: boolean
  signal_count: number
  distinct_sources: number
  avg_sentiment: number
  sources: string[]
  recent_summaries: string[]
  latest_signal_at: string | null
}

function TopTrendsStrip() {
  const [trends, setTrends] = useState<{ ticker: string; trend_score: number; direction: string; acceleration: string }[]>([])

  useEffect(() => {
    api.get('/api/trends', { params: { scope: 'movers', accelerating_only: false, limit: 8 } })
      .then(r => setTrends(r.data?.trends ?? []))
      .catch(() => setTrends([]))
  }, [])

  if (trends.length === 0) return null

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Radar size={14} className="text-amber-400" />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.momentum}>Top Trends</h2>
        </div>
        <Link to="/app/trend-radar" className="text-[11px] text-text-muted hover:text-text-primary underline">trend radar →</Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {trends.map(t => (
          <div key={t.ticker}
               className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded border border-border/40 hover:bg-surface-hover/30 text-xs">
            <Link to={`/app/stock/${encodeURIComponent(t.ticker)}`} className="flex items-center gap-1.5">
              <span className="font-mono font-bold text-blue-light">{t.ticker}</span>
              <span className={(t.trend_score ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                {fmtSigned(t.trend_score, 0)}
              </span>
              {t.acceleration === 'building' && <span className="text-[10px] text-emerald-400 cursor-help" title="Building — the move is still accelerating">▲bld</span>}
            </Link>
            <Link to={`/app/chart?symbol=${encodeURIComponent(t.ticker)}`}
                  title="Open signal-aware chart" className="text-text-muted hover:text-blue-light">📈</Link>
          </div>
        ))}
      </div>
    </div>
  )
}

const SHIFT_TAG: Record<string, { label: string; tone: string }> = {
  building_uptrend: { label: '🚀 building', tone: 'text-emerald-400' },
  building_downtrend: { label: '⚠️ building down', tone: 'text-rose-400' },
  fading_trend: { label: '🌥️ fading', tone: 'text-amber-400' },
  established_uptrend: { label: '📈 up', tone: 'text-emerald-400' },
  established_downtrend: { label: '📉 down', tone: 'text-rose-400' },
  divergence: { label: '⚠ divergence', tone: 'text-amber-400' },
}

type TicWarn = {
  level: 'calm' | 'elevated' | 'high' | 'severe'
  headline: string
  strategic_outflow_bn?: number
  tech_gbp_at_risk?: number
  tech_pct?: number
  exposed_positions?: { ticker: string; name: string; value_gbp: number }[]
  as_of?: string
}

function TicWarningBanner() {
  const [w, setW] = useState<TicWarn | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    api.get('/api/tic-warning')
      .then(r => setW(r.data ?? null))
      .catch(() => setW(null))
  }, [])

  // Only surface a real alert — stay silent when calm.
  if (!w || w.level === 'calm') return null

  const tone =
    w.level === 'severe' ? 'border-rose-500/50 bg-rose-500/10 text-rose-200'
      : w.level === 'high' ? 'border-orange-500/40 bg-orange-500/[0.07] text-orange-200'
      : 'border-amber-500/30 bg-amber-500/[0.06] text-amber-200'

  return (
    <button type="button" onClick={() => setExpanded(e => !e)}
            className={`w-full text-left rounded-xl border p-3 ${tone}`}>
      <div className="flex items-start gap-2">
        <span className="text-base shrink-0">{w.level === 'severe' ? '🚨' : '⚠️'}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Geopolitical capital-flight · {w.level}
            </span>
            {w.as_of && <span className="text-[11px] opacity-60">{w.as_of}</span>}
          </div>
          <p className="text-xs mt-0.5 leading-snug">{w.headline}</p>
          {expanded && (w.exposed_positions?.length ?? 0) > 0 && (
            <div className="mt-2 space-y-0.5">
              <p className="text-[11px] opacity-70">Most exposed (rate-sensitive / tech):</p>
              {(w.exposed_positions ?? []).slice(0, 6).map(p => (
                <div key={p.ticker} className="flex items-center justify-between text-[11px]">
                  <span className="font-mono">{p.ticker}</span>
                  <span className="opacity-80">£{Math.round(p.value_gbp ?? 0).toLocaleString('en-GB')}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] opacity-60 mt-1">{expanded ? 'tap to collapse' : 'tap for exposed positions'}</p>
        </div>
      </div>
    </button>
  )
}

function WhatsMovingStrip() {
  const [shifts, setShifts] = useState<{ ticker: string; category: string; trend_score: number | null; headline: string; days_building?: number }[]>([])

  useEffect(() => {
    api.get('/api/trends/shifts', { params: { scope: 'book', limit: 6 } })
      .then(r => setShifts(r.data?.shifts ?? []))
      .catch(() => setShifts([]))
  }, [])

  if (shifts.length === 0) return null

  return (
    <div className="card p-3 border-amber-500/20 bg-amber-500/[0.03]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-amber-400" />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.book}>What's Moving in Your Book</h2>
        </div>
        <Link to="/app/trend-radar" className="text-[11px] text-text-muted hover:text-text-primary underline">trend radar →</Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {shifts.map(s => {
          const tag = SHIFT_TAG[s.category] ?? { label: s.category, tone: 'text-text-muted' }
          return (
            <Link key={s.ticker} to={`/app/chart?symbol=${encodeURIComponent(s.ticker)}`}
                  title={s.headline}
                  className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded border border-border/40 hover:bg-surface-hover/30 text-xs">
              <span className="font-mono font-bold text-blue-light">{s.ticker}</span>
              <span className={(s.trend_score ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                {fmtSigned(s.trend_score, 0)}
              </span>
              <span className={`text-[10px] ${tag.tone}`}>{tag.label}</span>
              {(s.days_building ?? 0) >= 2 && (
                <span className="text-[10px] text-text-muted" title={`Same direction for ${s.days_building} days`}>{s.days_building}d</span>
              )}
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function PredictionMarketsStrip() {
  const [markets, setMarkets] = useState<{ ticker: string | null; implied_pct: number | null; summary: string; source: string }[]>([])

  useEffect(() => {
    api.get('/api/signals/prediction-markets', { params: { limit: 6 } })
      .then(r => setMarkets(r.data?.markets ?? []))
      .catch(() => setMarkets([]))
  }, [])

  if (markets.length === 0) return null

  const probTone = (pct: number | null) =>
    pct == null ? 'text-text-muted'
      : pct >= 70 ? 'text-emerald-400'
      : pct <= 30 ? 'text-rose-400'
      : 'text-amber-400'

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Percent size={14} className="text-amber-400" />
          <h2 className="panel-title">Prediction Markets</h2>
        </div>
        <Link to="/app/prediction-markets" className="text-[11px] text-text-muted hover:text-text-primary underline">all odds →</Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {markets.map((m, i) => (
          <Link key={i} to="/app/prediction-markets"
                title={m.summary}
                className="shrink-0 max-w-[220px] flex items-center gap-2 px-2.5 py-1 rounded border border-border/40 hover:bg-surface-hover/30 text-xs">
            <span className={`font-mono font-bold tabular-nums ${probTone(m.implied_pct)}`}>
              {fmtPct(m.implied_pct, 0)}
            </span>
            {m.ticker && <span className="font-mono text-blue-light">{m.ticker}</span>}
            <span className="text-text-muted truncate">
              {cleanMarketSummary(m.summary).slice(0, 48)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}

function LatestResearchStrip() {
  const [papers, setPapers] = useState<{ title: string; ticker: string | null; relevance: number | null }[]>([])

  useEffect(() => {
    api.get('/api/signals/research-papers', { params: { limit: 6 } })
      .then(r => setPapers(r.data?.papers ?? []))
      .catch(() => setPapers([]))
  }, [])

  if (papers.length === 0) return null

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-indigo-400" />
          <h2 className="panel-title">Latest Research</h2>
        </div>
        <Link to="/app/research-papers" className="text-[11px] text-text-muted hover:text-text-primary underline">all papers →</Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {papers.map((p, i) => (
          <Link key={i} to="/app/research-papers"
                title={p.title}
                className="shrink-0 max-w-[260px] flex items-center gap-2 px-2.5 py-1 rounded border border-border/40 hover:bg-surface-hover/30 text-xs">
            {p.relevance != null && (
              <span
                title={`Relevance to your book / active themes: ${fmtPct((p.relevance ?? NaN) * 100, 0)}`}
                className={`font-mono tabular-nums ${
                  p.relevance >= 0.7 ? 'text-emerald-400'
                  : p.relevance >= 0.4 ? 'text-amber-400'
                  : 'text-text-muted'
                }`}
              >
                {fmtPct((p.relevance ?? NaN) * 100, 0)}
              </span>
            )}
            {p.ticker && <span className="font-mono text-blue-light">{p.ticker}</span>}
            <span className="text-text-muted truncate">{(p.title ?? '').slice(0, 56)}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

function SignalsPulseBar() {
  const [counts, setCounts] = useState<{
    golden: number
    ideas: number
    catalyst: number
    live: number
    multiCat: number
  } | null>(null)
  const [playbook, setPlaybook] = useState<{ label: string; bias: string; kelly_scalar: number; bucket: string } | null>(null)

  useEffect(() => {
    api.get('/api/playbook')
      .then(r => setPlaybook({
        label: r.data?.playbook?.label ?? '',
        bias: r.data?.playbook?.bias ?? '',
        kelly_scalar: r.data?.playbook?.kelly_scalar ?? 1,
        bucket: r.data?.bucket ?? 'neutral',
      }))
      .catch(() => setPlaybook(null))
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [g, i, c, l, mc] = await Promise.allSettled([
          api.get('/api/signals/golden-setups?hours=48'),
          api.get('/api/signals/trade-ideas'),
          api.get('/api/signals/feed?limit=100&max_age_hours=12&source=catalyst_breakout'),
          api.get('/api/signals/feed?limit=100&max_age_hours=6'),
          api.get('/api/multi-category/live?min_categories=3&lookback_hours=24&limit=50'),
        ])
        if (cancelled) return
        setCounts({
          golden: g.status === 'fulfilled' ? (g.value.data?.count ?? 0) : 0,
          ideas: i.status === 'fulfilled' ? (i.value.data?.ideas?.length ?? 0) : 0,
          catalyst: c.status === 'fulfilled' ? (c.value.data?.signals?.length ?? 0) : 0,
          live: l.status === 'fulfilled' ? (l.value.data?.signals?.length ?? 0) : 0,
          multiCat: mc.status === 'fulfilled' ? (mc.value.data?.convergences?.length ?? 0) : 0,
        })
      } catch {
        if (!cancelled) setCounts(null)
      }
    }
    load()
    const iv = setInterval(load, 90_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  if (!counts) return null
  const total = counts.golden + counts.ideas + counts.catalyst
  if (total === 0) return null

  return (
    <div className="card px-4 py-2 flex items-center gap-4 flex-wrap text-xs">
      <span className="panel-title cursor-help" title={`Live counts of high-quality setups in the signal pipeline. ${JARGON_HELP.confluence}`}>Signals Pulse</span>
      {counts.golden > 0 && (
        <Link to="/app/trade-ideas" className="flex items-center gap-1 hover:opacity-80 cursor-help" title={JARGON_HELP.golden}>
          <span className="text-amber-400">🌟</span>
          <span className="font-bold text-amber-400">{counts.golden}</span>
          <span className="text-text-muted">golden</span>
        </Link>
      )}
      <Link to="/app/trade-ideas" className="flex items-center gap-1 hover:opacity-80">
        <Target size={12} className="text-blue-light" />
        <span className="font-bold text-text-primary">{counts.ideas}</span>
        <span className="text-text-muted">trade ideas</span>
      </Link>
      {counts.catalyst > 0 && (
        <Link to="/app/signals" className="flex items-center gap-1 hover:opacity-80 cursor-help" title={JARGON_HELP.catalyst}>
          <Zap size={12} className="text-emerald-400" />
          <span className="font-bold text-emerald-400">{counts.catalyst}</span>
          <span className="text-text-muted">catalyst breakouts</span>
        </Link>
      )}
      <Link to="/app/signals" className="flex items-center gap-1 hover:opacity-80">
        <Activity size={12} className="text-text-muted" />
        <span className="font-bold text-text-primary">{counts.live}</span>
        <span className="text-text-muted">live (6h)</span>
      </Link>
      {counts.multiCat > 0 && (
        <Link to="/app/trade-ideas" className="flex items-center gap-1 hover:opacity-80 cursor-help" title={JARGON_HELP.multiCat}>
          <span className="text-fuchsia-400">🔥</span>
          <span className="font-bold text-fuchsia-400">{counts.multiCat}</span>
          <span className="text-text-muted">multi-cat</span>
        </Link>
      )}
      {playbook && (
        <Link to="/app/daily?tab=alpha-brief" className="flex items-center gap-1 hover:opacity-80 cursor-help" title={`Playbook: ${playbook.label} · ${playbook.bias} bias · Kelly × ${fmtNum(playbook.kelly_scalar)}. ${JARGON_HELP.kelly}`}>
          <span className="text-indigo-300">📓</span>
          <span className="font-bold text-indigo-300">{playbook.bias}</span>
          <span className="text-text-muted font-mono">K{fmtNum(playbook.kelly_scalar)}×</span>
        </Link>
      )}
      <span className="ml-auto text-[11px] text-text-muted">auto-refreshes</span>
    </div>
  )
}

function BookSignalsWidget() {
  const [book, setBook] = useState<BookEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get('/api/signals/book-signals?hours=72')
        const entries: BookEntry[] = res.data?.book ?? []
        if (!cancelled) setBook(entries)
      } catch {
        if (!cancelled) setBook([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const iv = setInterval(load, 120_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  if (loading) return null
  if (book.length === 0) return null

  // Split into 'active' (has signals) and 'quiet' (no signals in 72h)
  const active = book.filter(b => b.signal_count > 0)
  const quiet = book.filter(b => b.signal_count === 0)

  if (active.length === 0 && quiet.length === 0) return null

  const display = showAll ? book : active.slice(0, 10)

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={14} className="text-amber-400" />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.book}>Your Book · Signals</h2>
          <span className="text-[11px] text-text-muted">
            {active.length} active · {quiet.length} quiet · 72h window
          </span>
        </div>
        {book.length > 10 && (
          <button
            type="button"
            onClick={() => setShowAll(s => !s)}
            className="text-[11px] text-text-muted hover:text-text-primary underline"
          >
            {showAll ? 'show top 10' : `show all ${book.length}`}
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {display.map(b => {
          const colour =
            b.avg_sentiment >= 0.3 ? 'border-l-emerald-500/60 bg-emerald-500/5'
            : b.avg_sentiment <= -0.3 ? 'border-l-rose-500/60 bg-rose-500/5'
            : b.signal_count > 0 ? 'border-l-border-light bg-surface-hover/30'
            : 'border-l-border/40'
          const sentimentTxt =
            b.avg_sentiment >= 0.3 ? 'text-emerald-400'
            : b.avg_sentiment <= -0.3 ? 'text-rose-400'
            : 'text-text-muted'
          return (
            <div
              key={b.ticker}
              className={`border-l-2 pl-3 py-1.5 flex items-center gap-2 text-xs ${colour}`}
            >
              <Link
                to={`/app/stock/${encodeURIComponent(b.ticker)}`}
                className="font-mono font-bold text-blue-light hover:underline w-20 shrink-0"
                title={b.name}
              >
                {b.ticker}
              </Link>
              <span className="text-text-muted text-[11px] w-16 shrink-0 font-mono">
                £{Math.round(b.value_gbp).toLocaleString('en-GB')}
              </span>
              {!b.discretionary && (
                <span
                  className="text-[10px] px-1 py-0.5 rounded bg-surface-hover text-text-muted shrink-0"
                  title="Non-discretionary (IFA managed) — info only"
                >
                  IFA
                </span>
              )}
              {b.signal_count > 0 ? (
                <>
                  <span className={`font-mono w-14 text-right font-semibold shrink-0 ${sentimentTxt}`}>
                    {fmtSigned(b.avg_sentiment)}
                  </span>
                  <span className="text-[11px] text-text-muted shrink-0 cursor-help" title={`${b.distinct_sources} independent sources produced ${b.signal_count} signals in the last 72h`}>
                    {b.distinct_sources}s / {b.signal_count} hits
                  </span>
                  <span className="text-text-secondary truncate flex-1 text-[11px]">
                    {b.recent_summaries[0] || b.sources.slice(0, 3).join(', ')}
                  </span>
                </>
              ) : (
                <span className="text-text-muted text-[11px] italic flex-1">
                  no signals in last 72h
                </span>
              )}
            </div>
          )
        })}
      </div>
      {!showAll && active.length > 10 && (
        <p className="text-[11px] text-text-muted mt-2">
          +{active.length - 10} more active in your book — click 'show all' above
        </p>
      )}
    </div>
  )
}

type ConvictionEntry = {
  ticker: string
  direction: 'LONG' | 'SHORT'
  conviction: number
  avg_sentiment: number
  avg_confidence: number
  signal_count: number
  distinct_sources: number
  sources: string[]
  recent_summary: string | null
  latest: string | null
  agreement_ratio?: number | null
  bullish_count?: number
  bearish_count?: number
}

const CONVICTION_DIR_KEY = 'fg.topConviction.dir'

function TopConvictionWidget() {
  const [data, setData] = useState<ConvictionEntry[]>([])
  const [loading, setLoading] = useState(true)
  // Persist the direction filter so it survives reloads. Lazy-read once; guard
  // against unavailable/throwing storage (private mode, quota, SSR).
  const [dir, setDir] = useState<'both' | 'long' | 'short'>(() => {
    try {
      const saved = localStorage.getItem(CONVICTION_DIR_KEY)
      if (saved === 'both' || saved === 'long' || saved === 'short') return saved
    } catch { /* storage unavailable — fall through to default */ }
    return 'both'
  })

  useEffect(() => {
    try { localStorage.setItem(CONVICTION_DIR_KEY, dir) } catch { /* ignore */ }
  }, [dir])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await api.get(`/api/signals/top-conviction?limit=10&direction=${dir}`)
        if (!cancelled) setData(res.data?.tickers ?? [])
      } catch {
        if (!cancelled) setData([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const iv = setInterval(load, 120_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [dir])

  if (loading && data.length === 0) return null
  if (data.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-emerald-400" />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.conviction}>Top Conviction</h2>
          <span
            className="text-[11px] text-text-muted cursor-help"
            title={'Research ranking, NOT a trade list. This ranks every ticker '
              + 'by |sentiment| x confidence x sqrt(sources) over 48h. The '
              + 'trading engine scores differently: a 6h window, engine-derived '
              + 'sources excluded, and it also requires price confirmation, a '
              + 'cost check and a tradeable symbol. A name can top this list '
              + 'and never be a trade — the two answer different questions.'}
          >
            across all feeds · 48h · research view, not the engine's gate
          </span>
        </div>
        <div
          role="tablist"
          aria-label="Direction filter"
          className="flex rounded-lg border border-border overflow-hidden text-[11px]"
        >
          {(['both', 'long', 'short'] as const).map(d => (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={dir === d ? 'true' : 'false'}
              onClick={() => setDir(d)}
              className={`px-2 py-0.5 transition-colors ${
                dir === d
                  ? d === 'long' ? 'bg-emerald-500/20 text-emerald-400'
                    : d === 'short' ? 'bg-rose-500/20 text-rose-400'
                    : 'bg-blue/20 text-blue-light'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        {data.map((e, i) => {
          const colour =
            e.direction === 'LONG' ? 'text-emerald-400'
            : 'text-rose-400'
          return (
            <div
              key={`${e.ticker}-${i}`}
              className="flex items-center gap-2 text-xs border-b border-border/30 pb-1.5 last:border-b-0"
            >
              <span className="text-text-muted text-[11px] w-5 shrink-0 font-mono">#{i + 1}</span>
              <Link
                to={`/app/stock/${encodeURIComponent(e.ticker)}`}
                className="font-mono font-bold text-blue-light hover:underline w-20 shrink-0"
              >
                {e.ticker}
              </Link>
              <span className={`font-mono font-bold w-14 ${colour} shrink-0`}>
                {e.direction}
              </span>
              <span className={`font-mono w-12 text-right font-semibold shrink-0 ${colour}`}>
                {fmtSigned(e.avg_sentiment)}
              </span>
              <span className="text-[11px] text-text-muted shrink-0 cursor-help" title={`${e.distinct_sources} independent sources produced ${e.signal_count} signals in the last 48h`}>
                {e.distinct_sources}s · {e.signal_count} hits
              </span>
              {e.agreement_ratio != null && ((e.bullish_count ?? 0) + (e.bearish_count ?? 0)) >= 2 && (
                <span
                  title={`${Math.round((e.agreement_ratio ?? 0) * 100)}% of directional signals agree (${e.bullish_count ?? 0} bull / ${e.bearish_count ?? 0} bear). 100% = unanimous, 50% = evenly split.`}
                  className={`text-[11px] font-mono font-bold shrink-0 px-1 rounded ${
                    (e.agreement_ratio ?? 0) >= 0.8 ? 'bg-emerald-500/15 text-emerald-400'
                    : (e.agreement_ratio ?? 0) >= 0.65 ? 'bg-amber-500/15 text-amber-400'
                    : 'bg-rose-500/15 text-rose-400'
                  }`}
                >
                  {Math.round((e.agreement_ratio ?? 0) * 100)}% agree
                </span>
              )}
              <span className="text-text-secondary truncate flex-1 text-[11px]">
                {e.recent_summary || e.sources.slice(0, 3).join(', ')}
              </span>
              <span
                title="Convergence score = |sentiment| × confidence × √sources. Higher = stronger multi-feed agreement."
                className={`text-[11px] font-mono font-bold shrink-0 ${
                  (e.conviction ?? 0) >= 1.5 ? 'text-emerald-400'
                  : (e.conviction ?? 0) >= 0.8 ? 'text-amber-400'
                  : 'text-text-muted'
                }`}
              >
                {fmtNum(e.conviction)}
              </span>
            </div>
          )
        })}
      </div>
      <p className="mt-2 text-[11px] text-text-muted cursor-help" title={JARGON_HELP.sentiment}>
        Score = |sentiment| × confidence × √sources. Independent of your book —
        shows the best setups ANYWHERE right now.
      </p>
    </div>
  )
}

type BlotterItem = {
  id: number
  ticker: string
  direction: 'LONG' | 'SHORT' | 'WATCH'
  status: 'new' | 'saved' | 'acted' | 'closed' | 'dismissed'
  conviction?: number | null
  entry_price?: number | null
  stop_price?: number | null
  target_price?: number | null
  suggested_gbp?: number | null
  current_price?: number | null
  pnl_vs_entry_pct?: number | null
  created_at: string
  acted_price?: number | null
  acted_quantity?: number | null
}

// ── Shared blotter snapshot ──────────────────────────────────────────────────
// ActiveBlotterWidget and BookStateStrip both poll the same blotter endpoints
// on the same 120s cadence. This module-level cache dedupes concurrent/near
// -in-time callers into ONE set of network round-trips shared by both widgets.
type BlotterSnapshot = {
  counts: { saved?: number; acted?: number; closed?: number }
  acted: BlotterItem[]
  saved: BlotterItem[]
  ok: boolean
}
let _blotterSnapshotCache: { at: number; promise: Promise<BlotterSnapshot> } | null = null
const BLOTTER_SNAPSHOT_TTL_MS = 60_000

function fetchBlotterSnapshot(): Promise<BlotterSnapshot> {
  const now = Date.now()
  if (_blotterSnapshotCache && now - _blotterSnapshotCache.at < BLOTTER_SNAPSHOT_TTL_MS) {
    return _blotterSnapshotCache.promise
  }
  const promise = (async (): Promise<BlotterSnapshot> => {
    const [statsRes, actedRes, savedRes] = await Promise.allSettled([
      api.get('/api/blotter/stats'),
      api.get('/api/blotter/ideas?status=acted&limit=200'),
      api.get('/api/blotter/ideas?status=saved&limit=200'),
    ])
    return {
      counts: statsRes.status === 'fulfilled' ? (statsRes.value.data?.counts ?? {}) : {},
      acted: actedRes.status === 'fulfilled' ? (actedRes.value.data?.ideas ?? []) : [],
      saved: savedRes.status === 'fulfilled' ? (savedRes.value.data?.ideas ?? []) : [],
      ok: statsRes.status === 'fulfilled' || actedRes.status === 'fulfilled' || savedRes.status === 'fulfilled',
    }
  })()
  _blotterSnapshotCache = { at: now, promise }
  return promise
}

function ActiveBlotterWidget() {
  const [items, setItems] = useState<BlotterItem[]>([])
  const [loading, setLoading] = useState(true)
  const [counts, setCounts] = useState<{ saved?: number; acted?: number; closed?: number }>({})

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const snap = await fetchBlotterSnapshot()  // shared with BookStateStrip
        if (cancelled) return
        // Acted first (open positions), then saved (watching)
        setItems([...snap.acted, ...snap.saved].slice(0, 8))
        setCounts(snap.counts)
      } catch {
        if (!cancelled) setItems([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const iv = setInterval(load, 120_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  if (loading) return null
  if (items.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart2 size={14} className="text-amber-400" />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.blotter}>Your Blotter</h2>
          <span className="text-[11px] text-text-muted">
            {counts.acted ?? 0} open · {counts.saved ?? 0} watching
          </span>
        </div>
        <Link to="/app/blotter" className="text-[11px] text-text-muted hover:text-text-primary underline">
          open blotter →
        </Link>
      </div>
      <div className="space-y-1.5">
        {items.map(b => {
          const pnlColour =
            b.pnl_vs_entry_pct == null ? 'text-text-muted'
            : b.pnl_vs_entry_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'
          const statusColour: Record<string, string> = {
            saved: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
            acted: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
            closed: 'bg-surface-hover text-text-secondary border-border',
            new: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
            dismissed: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
          }
          return (
            <div
              key={b.id}
              className="flex items-center gap-2 text-xs border-b border-border/30 pb-1.5 last:border-b-0"
            >
              <Link
                to={`/app/stock/${encodeURIComponent(b.ticker)}`}
                className="font-mono font-bold text-blue-light hover:underline w-20 shrink-0"
              >
                {b.ticker}
              </Link>
              <span className={`font-mono w-14 text-center shrink-0 ${
                b.direction === 'LONG' ? 'text-emerald-400'
                : b.direction === 'SHORT' ? 'text-rose-400'
                : 'text-text-muted'
              }`}>
                {b.direction}
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusColour[b.status]} shrink-0`}>
                {b.status}
              </span>
              {b.entry_price != null && (
                <span className="text-text-muted text-[11px] shrink-0">
                  @ <span className="font-mono text-text-secondary">{b.entry_price.toFixed(2)}</span>
                </span>
              )}
              {b.current_price != null && (
                <span className="text-text-muted text-[11px] shrink-0">
                  Now <span className="font-mono text-text-primary">{b.current_price.toFixed(2)}</span>
                </span>
              )}
              <span className={`font-mono w-14 text-right font-semibold shrink-0 ${pnlColour}`}>
                {b.pnl_vs_entry_pct != null
                  ? `${b.pnl_vs_entry_pct >= 0 ? '+' : ''}${b.pnl_vs_entry_pct.toFixed(1)}%`
                  : '—'}
              </span>
              {b.suggested_gbp != null && (
                <span className="text-text-muted text-[11px] ml-auto shrink-0">
                  £{Math.round(b.suggested_gbp).toLocaleString('en-GB')}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ActionPlanPanel() {
  const [recs, setRecs] = useState<StrategyRecommendation[]>([])
  const [health, setHealth] = useState<PortfolioHealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const [recsRes, healthRes] = await Promise.allSettled([
        getStrategyRecommendations(),
        getPortfolioHealth(),
      ])
      if (recsRes.status === 'fulfilled') setRecs(recsRes.value.recommendations.slice(0, 3))
      if (healthRes.status === 'fulfilled') setHealth(healthRes.value)
      if (recsRes.status === 'rejected' && healthRes.status === 'rejected') setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const gradeColor =
    health?.grade === 'A' ? '#10b981' :
    health?.grade === 'B' ? '#3b82f6' :
    health?.grade === 'C' ? '#f59e0b' :
    health?.grade === 'D' ? '#fb923c' : '#f43f5e'

  return (
    <div className="card p-5 lg:col-span-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-amber" aria-hidden />
          <h2 className="panel-title">Action Plan</h2>
          {health && (
            <span
              className="text-xs font-bold font-mono px-1.5 py-0.5 rounded cursor-help"
              style={{ color: gradeColor, background: `${gradeColor}20` }}
              title={JARGON_HELP.healthScore}
            >
              Health {health.score}/100 · {health.grade}
            </span>
          )}
        </div>
        <Link
          to="/app/portfolio?tab=strategy"
          className="text-xs text-blue-light hover:underline flex items-center gap-1"
        >
          All recommendations <ArrowRight size={12} />
        </Link>
      </div>

      {loading ? (
        <ul className="space-y-2" aria-busy="true">
          {[...Array(3)].map((_, i) => (
            <li key={i} className="h-12 rounded-lg bg-surface-hover/40 animate-pulse" />
          ))}
        </ul>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <p className="text-sm text-text-muted">Could not load recommendations</p>
          <button type="button" onClick={load} className="text-xs text-blue-light hover:underline flex items-center gap-1">
            <RefreshCw size={11} aria-hidden /> Try again
          </button>
        </div>
      ) : recs.length > 0 ? (
        <ul className="space-y-3" aria-label="Top strategy recommendations">
          {recs.map(rec => (
            <li key={rec.id} className="flex items-start gap-3">
              <div className="flex flex-col gap-1 shrink-0 mt-0.5">
                <span className={`badge text-xs cursor-help ${PRIORITY_BADGE[rec.priority] ?? 'badge'}`} title={JARGON_HELP.priority}>
                  P{rec.priority}
                </span>
                <span className={`badge text-xs ${ACTION_BADGE[rec.action] ?? 'badge'}`}>
                  {rec.action}
                </span>
                <PortfolioFitBadge fit={rec.portfolio_fit} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary leading-tight">{rec.title}</p>
                <p className="text-xs text-text-muted mt-0.5 truncate">{rec.account}</p>
                {rec.current_weight !== null && rec.target_weight !== null && (
                  <p className="text-xs text-text-muted mt-0.5">
                    {rec.current_weight}% → {rec.target_weight}%
                    {rec.estimated_value ? ` · ${rec.estimated_value}` : ''}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
          <Zap size={28} className="text-text-muted opacity-30" aria-hidden />
          <p className="text-sm text-text-muted">No urgent actions — portfolio looks balanced</p>
        </div>
      )}
    </div>
  )
}

// ── Today's Movers card ───────────────────────────────────────────────────────

function MoverRow({ m, side }: { m: TopMoverEntry; side: 'gain' | 'loss' }) {
  const sign      = side === 'gain' ? '+' : ''
  const pctClass  = side === 'gain' ? 'text-emerald' : 'text-rose-light'
  const changePct = m.change_pct ?? 0
  const changeVal = m.change_value ?? 0
  return (
    <li className="flex items-center justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-xs font-bold text-text-primary shrink-0 w-[62px] truncate">
          {m.ticker}
        </span>
        <span className="text-xs text-text-muted truncate">{m.name}</span>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-xs font-bold font-mono ${pctClass}`}>
          {sign}{changePct.toFixed(2)}%
        </p>
        <p className="text-[11px] text-text-muted font-mono">
          {sign}£{Math.abs(changeVal).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
        </p>
      </div>
    </li>
  )
}

function TopMoversCard() {
  const [gainers, setGainers] = useState<TopMoverEntry[]>([])
  const [losers,  setLosers]  = useState<TopMoverEntry[]>([])
  const [hasData, setHasData] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(false)
    getTopMovers()
      .then(r => {
        setGainers(r.gainers ?? [])
        setLosers(r.losers ?? [])
        setHasData(r.has_data ?? false)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const noMovers = !loading && hasData && gainers.length === 0 && losers.length === 0

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <BarChart2 size={16} className="text-blue-light" aria-hidden />
          <h2 className="panel-title">Today's Movers</h2>
        </div>
        <Link
          to="/app/portfolio?tab=positions"
          className="text-xs text-blue-light hover:underline flex items-center gap-1"
        >
          All positions <ArrowRight size={12} />
        </Link>
      </div>
      <p className="text-[11px] text-text-muted mb-4">From your portfolio</p>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-8 rounded bg-surface-hover/50" />
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <p className="text-sm text-text-muted">Could not load movers</p>
          <button type="button" onClick={load} className="text-xs text-blue-light hover:underline flex items-center gap-1">
            <RefreshCw size={11} aria-hidden /> Try again
          </button>
        </div>
      ) : !hasData || noMovers ? (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
          <BarChart2 size={28} className="text-text-muted opacity-30" aria-hidden />
          <p className="text-sm text-text-muted">Markets closed</p>
          <p className="text-xs text-text-muted opacity-60">Price data unavailable — check back on a trading day</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Gainers */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <TrendingUp size={12} className="text-emerald" aria-hidden />
              <span className="text-xs font-semibold text-emerald uppercase tracking-wide">Top Gainers</span>
            </div>
            {gainers.length > 0 ? (
              <ul aria-label="Top gainers from your portfolio">
                {gainers.map(m => <MoverRow key={m.ticker} m={m} side="gain" />)}
              </ul>
            ) : (
              <p className="text-xs text-text-muted py-3">No gainers today</p>
            )}
          </div>
          {/* Losers */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <TrendingDown size={12} className="text-rose-light" aria-hidden />
              <span className="text-xs font-semibold text-rose-light uppercase tracking-wide">Top Losers</span>
            </div>
            {losers.length > 0 ? (
              <ul aria-label="Top losers from your portfolio">
                {losers.map(m => <MoverRow key={m.ticker} m={m} side="loss" />)}
              </ul>
            ) : (
              <p className="text-xs text-text-muted py-3">No losers today</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Upcoming Dividends card ───────────────────────────────────────────────────

function UpcomingDividendsCard() {
  const [items,   setItems]   = useState<UpcomingDividend[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(false)
    getUpcomingDividends()
      .then(r => setItems(r ?? []))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  function fmtExDate(iso: string) {
    try {
      return format(new Date(iso), 'd MMM yyyy')
    } catch {
      return iso
    }
  }

  function daysUntil(iso: string) {
    try {
      const diff = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
      if (diff === 0) return 'today'
      if (diff === 1) return 'tomorrow'
      return `in ${diff}d`
    } catch {
      return ''
    }
  }

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-emerald" aria-hidden />
          <h2 className="panel-title">Upcoming Dividends</h2>
        </div>
        <Link
          to="/app/portfolio?tab=positions"
          className="text-xs text-blue-light hover:underline flex items-center gap-1"
        >
          All positions <ArrowRight size={12} />
        </Link>
      </div>
      <p className="text-[11px] text-text-muted mb-4 cursor-help" title={JARGON_HELP.exDividend}>Next ex-dividend dates for your holdings</p>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-8 rounded bg-surface-hover/50" />
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <p className="text-sm text-text-muted">Could not load dividend data</p>
          <button type="button" onClick={load} className="text-xs text-blue-light hover:underline flex items-center gap-1">
            <RefreshCw size={11} aria-hidden /> Try again
          </button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<CalendarDays size={28} />}
          message="No upcoming ex-dividend dates found"
          sub="Holdings with dividend data will appear here"
        />
      ) : (
        <ul className="space-y-2.5" aria-label="Upcoming ex-dividend dates">
          {items.map(item => (
            <li
              key={item.ticker}
              className="flex items-center justify-between gap-3 py-1.5 border-b border-border last:border-0"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs font-bold text-text-primary shrink-0 w-[62px] truncate">
                  {item.ticker}
                </span>
                <span className="text-xs text-text-muted truncate">{item.name}</span>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-mono text-text-primary">{fmtExDate(item.ex_date)}</p>
                <div className="flex items-center gap-2 justify-end mt-0.5">
                  <span className="text-[11px] text-text-muted">{daysUntil(item.ex_date)}</span>
                  {item.yield_pct != null && (
                    <span className="text-[11px] text-emerald font-mono">{fmtPct(item.yield_pct, 1)}</span>
                  )}
                  {item.est_payment != null && (
                    <span className="text-[11px] text-text-muted font-mono">
                      ~£{item.est_payment.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Allocation donut chart ────────────────────────────────────────────────────

// Hex colours that correspond to the Tailwind palette names used in the spec
const TYPE_COLORS: Record<string, string> = {
  SIPP:        '#3b82f6', // blue-500
  ISA:         '#10b981', // emerald-500
  GIA:         '#f59e0b', // amber-500
  Crypto:      '#a855f7', // purple-500
  Bank:        '#a1a1aa', // zinc-400
  'Real Estate': '#f97316', // orange-500
  Property:    '#f97316',
  Liabilities: '#ef4444', // red-500
}

const OWNER_COLORS: Record<string, string> = {
  Simon: '#3b82f6', // blue-500
  Unman: '#ec4899', // pink-500
  Joint: '#f59e0b', // amber-500
}

const FALLBACK_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#a855f7',
  '#f97316', '#ef4444', '#06b6d4', '#e879f9',
]

interface DonutSlice {
  label: string
  value: number
  color: string
}

/** Pure-SVG donut chart using stroke-dasharray technique. */
function DonutChart({ slices, total }: { slices: DonutSlice[]; total: number }) {
  const R = 54          // radius of the stroke circle
  const CX = 70         // viewBox centre
  const CY = 70
  const CIRCUMFERENCE = 2 * Math.PI * R
  const GAP = 1.5       // gap between segments in stroke-units

  // Build cumulative offsets
  let cumulative = 0
  const segments = slices.map(s => {
    const fraction = total > 0 ? s.value / total : 0
    // dasharray: painted portion, then the rest of the circle
    const painted = Math.max(fraction * CIRCUMFERENCE - GAP, 0)
    const offset = CIRCUMFERENCE - cumulative * CIRCUMFERENCE
    cumulative += fraction
    return { ...s, painted, offset }
  })

  return (
    <svg
      viewBox="0 0 140 140"
      className="w-28 h-28 sm:w-40 sm:h-40 shrink-0"
      role="img"
      aria-label="Portfolio allocation donut chart"
    >
      {/* Background ring */}
      <circle
        cx={CX} cy={CY} r={R}
        fill="none"
        stroke="rgba(148,163,184,0.12)"
        strokeWidth={16}
      />
      {segments.map((seg, i) => (
        <circle
          key={i}
          cx={CX} cy={CY} r={R}
          fill="none"
          stroke={seg.color}
          strokeWidth={16}
          strokeDasharray={`${seg.painted} ${CIRCUMFERENCE}`}
          strokeDashoffset={seg.offset}
          strokeLinecap="butt"
          transform={`rotate(-90 ${CX} ${CY})`}
        >
          <title>{seg.label}: {fmtGBP(seg.value)}</title>
        </circle>
      ))}
      {/* Centre text */}
      <text x={CX} y={CY - 8} textAnchor="middle" className="fill-text-primary" fontSize={10} fontWeight="600">
        Total
      </text>
      <text x={CX} y={CY + 7} textAnchor="middle" className="fill-text-primary" fontSize={11} fontWeight="700">
        {total >= 1_000_000
          ? `£${(total / 1_000_000).toFixed(2)}M`
          : total >= 1_000
            ? `£${(total / 1_000).toFixed(0)}k`
            : fmtGBP(total)}
      </text>
    </svg>
  )
}

const ASSET_CLASS_COLORS: Record<string, string> = {
  'Equity':       '#3b82f6',  // blue
  'Fixed Income': '#f59e0b',  // amber
  'Cash':         '#10b981',  // emerald
  'Real Estate':  '#a855f7',  // purple
  'Crypto':       '#f97316',  // orange
  'Commodity':    '#eab308',  // yellow
  'Liabilities':  '#ef4444',  // red
  'Other':        '#6b7280',  // grey
}

function AllocationWidget({ summary }: { summary: import('@/lib/types').HouseholdSummary }) {
  const [view, setView] = useState<'asset_class' | 'type' | 'owner'>('asset_class')

  const typeSlices: DonutSlice[] = (summary.by_type ?? [])
    .filter(t => t.total_value > 0)
    .sort((a, b) => b.total_value - a.total_value)
    .map((t, i) => ({
      label: t.account_type,
      value: t.total_value,
      color: TYPE_COLORS[t.account_type] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }))

  const ownerSlices: DonutSlice[] = (summary.by_owner ?? [])
    .filter(o => o.total_value > 0)
    .sort((a, b) => b.total_value - a.total_value)
    .map((o, i) => ({
      label: o.owner,
      value: o.total_value,
      color: OWNER_COLORS[o.owner] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }))

  const assetClassSlices: DonutSlice[] = (summary.by_asset_class ?? [])
    .filter(c => c.total_value > 0 && c.asset_class !== 'Liabilities')
    .sort((a, b) => b.total_value - a.total_value)
    .map((c, i) => ({
      label: c.asset_class,
      value: c.total_value,
      color: ASSET_CLASS_COLORS[c.asset_class] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }))

  const slices =
    view === 'asset_class' ? assetClassSlices
    : view === 'type' ? typeSlices
    : ownerSlices
  const total = slices.reduce((s, x) => s + x.value, 0)

  return (
    <div className="card p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart2 size={16} className="text-blue-light" aria-hidden />
          <h2 className="panel-title">Allocation</h2>
        </div>
        {/* Toggle */}
        <div
          role="tablist"
          aria-label="Allocation view"
          className="flex rounded-lg border border-border overflow-hidden text-xs"
        >
          <button
            role="tab"
            type="button"
            onClick={() => setView('asset_class')}
            className={`px-3 py-1 transition-colors ${
              view === 'asset_class'
                ? 'bg-blue/20 text-blue-light font-medium'
                : 'text-text-muted hover:text-text-secondary'
            }`}
            aria-selected={view === 'asset_class' ? 'true' : 'false'}
          >
            By Asset Class
          </button>
          <button
            role="tab"
            type="button"
            onClick={() => setView('type')}
            className={`px-3 py-1 transition-colors border-l border-border ${
              view === 'type'
                ? 'bg-blue/20 text-blue-light font-medium'
                : 'text-text-muted hover:text-text-secondary'
            }`}
            aria-selected={view === 'type' ? 'true' : 'false'}
            title={JARGON_HELP.wrapper}
          >
            By Wrapper
          </button>
          <button
            role="tab"
            type="button"
            onClick={() => setView('owner')}
            className={`px-3 py-1 transition-colors border-l border-border ${
              view === 'owner'
                ? 'bg-blue/20 text-blue-light font-medium'
                : 'text-text-muted hover:text-text-secondary'
            }`}
            aria-selected={view === 'owner' ? 'true' : 'false'}
          >
            By Owner
          </button>
        </div>
      </div>

      {slices.length === 0 ? (
        <EmptyState icon={<BarChart2 size={28} />} message="No account data yet" sub="Add accounts in Settings to see allocation" />
      ) : (
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start">
          <DonutChart slices={slices} total={total} />

          {/* Legend */}
          <ul className="flex-1 space-y-2 min-w-0 w-full" aria-label="Allocation legend">
            {slices.map(s => {
              const pct = total > 0 ? (s.value / total) * 100 : 0
              return (
                <li key={s.label} className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ backgroundColor: s.color }}
                    aria-hidden
                  />
                  <span className="text-xs text-text-secondary truncate flex-1">{s.label}</span>
                  <span className="text-xs font-mono text-text-muted shrink-0">{pct.toFixed(1)}%</span>
                  <span className="text-xs font-mono text-text-primary shrink-0">
                    {s.value >= 1_000_000
                      ? `£${(s.value / 1_000_000).toFixed(2)}M`
                      : `£${s.value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Proactive Insights Widget ─────────────────────────────────────────────────

const PRIORITY_ICON: Record<string, React.ReactNode> = {
  high:   <span aria-label="High priority" title="High">🔴</span>,
  medium: <span aria-label="Medium priority" title="Medium">🟡</span>,
  low:    <span aria-label="Low priority" title="Low">🟢</span>,
}

// Theme-aware badge system (see index.css) — the old bg-*-900 chips were
// unreadable on the light theme.
const CATEGORY_BADGE_CLASS: Record<string, string> = {
  market:      'badge-blue',
  tax:         'badge-purple',
  risk:        'badge-red',
  opportunity: 'badge-green',
  action:      'badge-amber',
  info:        'badge-neutral',
}

function ProactiveInsightsWidget() {
  const [items, setItems]   = useState<Insight[]>([])
  const [loading, setLoading] = useState(true)
  const [dismissing, setDismissing] = useState<Set<string>>(new Set())

  useEffect(() => {
    getInsights()
      .then(r => setItems((r.insights ?? []).slice(0, 5)))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleDismiss = async (id: string) => {
    setDismissing(prev => new Set(prev).add(id))
    try {
      await dismissInsight(id)
      setItems(prev => prev.filter(i => i.id !== id))
    } catch {
      // Keep it visible if the dismiss call fails
    } finally {
      setDismissing(prev => { const n = new Set(prev); n.delete(id); return n })
    }
  }

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-amber" aria-hidden />
          <h2 className="panel-title">Proactive Insights</h2>
          {!loading && items.length > 0 && (
            <span className="badge-red text-xs font-semibold">
              {items.filter(i => i.priority === 'high').length > 0
                ? `${items.filter(i => i.priority === 'high').length} urgent`
                : `${items.length}`}
            </span>
          )}
        </div>
        <Link
          to="/app/ifa-meeting"
          className="text-xs text-blue-light hover:underline flex items-center gap-1"
        >
          IFA prep <ArrowRight size={12} />
        </Link>
      </div>

      {loading ? (
        <ul className="space-y-2" aria-busy="true">
          {[...Array(3)].map((_, i) => (
            <li key={i} className="h-12 rounded-lg bg-surface-hover/40 animate-pulse" />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Zap size={28} />}
          message="No active insights"
          sub="Insights appear here when the engine detects portfolio actions"
        />
      ) : (
        <ul className="space-y-2.5" aria-label="Proactive insights">
          {items.map(ins => (
            <li
              key={ins.id}
              className="flex items-start gap-2.5 rounded-lg border border-border bg-surface/40 px-3 py-2.5"
            >
              <span className="text-sm mt-0.5 shrink-0">{PRIORITY_ICON[ins.priority] ?? '⚪'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-text-primary leading-snug">{ins.title}</p>
                  <span
                    className={`text-[11px] font-medium ${
                      CATEGORY_BADGE_CLASS[ins.category] ?? CATEGORY_BADGE_CLASS.info
                    }`}
                  >
                    {ins.category}
                  </span>
                </div>
                <p className="text-xs text-text-muted mt-0.5 leading-relaxed line-clamp-2">
                  {ins.message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDismiss(ins.id)}
                disabled={dismissing.has(ins.id)}
                className="btn-ghost p-1 rounded text-text-muted hover:text-text-primary shrink-0 disabled:opacity-40"
                aria-label={`Dismiss insight: ${ins.title}`}
                title="Dismiss"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Dashboard widget preferences ─────────────────────────────────────────────

const WIDGET_LABELS: Record<string, string> = {
  kpi:               'KPI Row',
  fundControl:       'Fund Control',
  insights:          'Smart Insights',
  proactiveInsights: 'Proactive Insights',
  chart:             'Performance Chart',
  allocation:        'Allocation',
  strategyDesk:      'Strategy Desk',
  riskDesk:          'Risk Desk',
  livePnl:           'Live P&L',
  intelligence:      'Market Intelligence',
  topMovers:         "Today's Movers",
  dividends:         'Upcoming Dividends',
  topSignals:        'Top Signals',
  convergences:      'Convergences',
  tradeIdeas:        'Trade Ideas',
  riskRadar:         'Risk Radar',
  proWidgets:        'Pro widgets (Signals Pulse + Kelly)',
}

type WidgetKey = keyof typeof WIDGET_LABELS

const DEFAULT_WIDGETS: Record<WidgetKey, boolean> = {
  kpi:               true,
  fundControl:       true,
  insights:          true,
  proactiveInsights: true,
  chart:             true,
  allocation:        true,
  strategyDesk:      true,
  riskDesk:          true,
  livePnl:           true,
  intelligence:      true,
  topMovers:         true,
  dividends:         true,
  topSignals:        true,
  convergences:      true,
  tradeIdeas:        true,
  riskRadar:         true,
  // Quant surfaces (Signals Pulse counts bar + Kelly playbook chip) are
  // pro-only: default OFF so the landing page reads as guidance. Opt in via
  // the settings gear — "Show pro widgets".
  proWidgets:        false,
}

function useWidgetPrefs() {
  const [prefs, setPrefs] = useState<Record<WidgetKey, boolean>>(() => {
    try {
      const stored = localStorage.getItem('av-dashboard-widgets')
      if (stored) return { ...DEFAULT_WIDGETS, ...JSON.parse(stored) }
    } catch { /* ignore */ }
    return DEFAULT_WIDGETS
  })

  const toggle = (key: WidgetKey) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: !prev[key] }
      localStorage.setItem('av-dashboard-widgets', JSON.stringify(next))
      return next
    })
  }

  return { prefs, toggle }
}

/** Thin "Show X" bar shown when a section is hidden. */
function ShowBar({ label, onShow }: { label: string; onShow: () => void }) {
  return (
    <button
      type="button"
      onClick={onShow}
      className="w-full flex items-center gap-2 px-4 py-1.5 rounded-lg border border-dashed border-border
                 text-xs text-text-muted hover:text-text-secondary hover:border-border-light transition-colors"
      aria-label={`Show ${label}`}
    >
      <ChevronDown size={12} aria-hidden />
      Show {label}
    </button>
  )
}

/** Settings gear panel — renders as a floating popover. */
function WidgetSettingsPanel({
  prefs,
  toggle,
  onClose,
}: {
  prefs: Record<WidgetKey, boolean>
  toggle: (k: WidgetKey) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  return (
    <div
      ref={ref}
      className="absolute right-0 top-10 z-50 w-56 card p-4 shadow-xl space-y-2"
      role="dialog"
      aria-label="Dashboard widget settings"
    >
      <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Visible sections</p>
      {(Object.keys(WIDGET_LABELS) as WidgetKey[]).map(key => (
        <label key={key} className="flex items-center gap-3 cursor-pointer group">
          <span className="relative inline-flex items-center">
            <input
              type="checkbox"
              checked={prefs[key]}
              onChange={() => toggle(key)}
              className="sr-only peer"
            />
            <span className="w-8 h-4 bg-surface-hover rounded-full peer-checked:bg-blue transition-colors" />
            <span className="absolute left-0.5 top-0.5 w-3 h-3 bg-text-primary rounded-full transition-transform peer-checked:translate-x-4" />
          </span>
          <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
            {WIDGET_LABELS[key]}
          </span>
          {prefs[key]
            ? <Eye size={12} className="ml-auto text-text-muted" aria-hidden />
            : <EyeOff size={12} className="ml-auto text-text-muted opacity-40" aria-hidden />
          }
        </label>
      ))}
    </div>
  )
}

// ── Recently Viewed strip ─────────────────────────────────────────────────────

function RecentlyViewedStrip() {
  const { recentTickers, removeTicker } = useRecentActivity()
  const navigate = useNavigate()

  if (recentTickers.length === 0) return null

  return (
    <div className="flex items-center gap-3 overflow-x-auto scrollbar-none py-1">
      <div className="flex items-center gap-1.5 text-xs text-text-muted shrink-0">
        <Clock size={12} aria-hidden />
        <span>Recently viewed</span>
      </div>
      {recentTickers.map(ticker => (
        <div
          key={ticker}
          className="group flex items-center gap-1 bg-surface-card border border-border rounded-lg px-2.5 py-1 shrink-0"
        >
          <button
            type="button"
            onClick={() => navigate(`/app/stock/${ticker}`)}
            className="text-xs font-mono font-semibold text-text-secondary hover:text-text-primary transition-colors"
            aria-label={`View ${ticker}`}
          >
            {ticker}
          </button>
          <button
            type="button"
            onClick={() => removeTicker(ticker)}
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-text-muted hover:text-text-primary transition-all"
            aria-label={`Remove ${ticker} from recently viewed`}
          >
            <X size={10} />
          </button>
        </div>
      ))}
    </div>
  )
}

// ── Top Signals widget ───────────────────────────────────────────────────────

interface DigestSignal {
  ticker: string | null
  source: string
  summary: string
  score: number
  sentiment: number
}

function TradeIdeasWidget() {
  const [ideas, setIdeas] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/api/signals/trade-ideas')
      .then(r => setIdeas((r.data?.ideas ?? []).slice(0, 5)))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={16} className="text-amber" aria-hidden />
          <h2 className="panel-title">Trade Ideas</h2>
        </div>
        <Link to="/app/trade-ideas" className="text-xs text-blue-light hover:underline flex items-center gap-1">
          All ideas <ArrowRight size={12} />
        </Link>
      </div>
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-8 rounded bg-surface-hover/40 animate-pulse" />)}</div>
      ) : ideas.length === 0 ? (
        <p className="text-sm text-text-muted py-4 text-center">No trade ideas yet</p>
      ) : (
        <ul className="space-y-1.5">
          {ideas.map((idea: any) => {
            // Portfolio-resolved action (HOLD/TRIM when the position is full);
            // fail-soft to direction for old payloads.
            const action = String(idea.resolved_action ?? idea.direction ?? '').toUpperCase()
            return (
            <li key={idea.ticker} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
              <div className="flex items-center gap-2">
                {action === 'LONG' ? <TrendingUp size={12} className="text-emerald-400" />
                  : action === 'HOLD' || action === 'TRIM' || action === 'WATCH' ? <Minus size={12} className={action === 'TRIM' ? 'text-amber-400' : 'text-sky-400'} />
                  : <TrendingDown size={12} className="text-rose-400" />}
                <Link to={`/app/stock/${idea.ticker}`} className="text-sm font-bold text-blue-light hover:underline">{idea.ticker}</Link>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                  action === 'LONG' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                  : action === 'HOLD' ? 'bg-sky-500/15 text-sky-400 border-sky-500/30'
                  : action === 'TRIM' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                  : action === 'WATCH' ? 'bg-surface-hover text-text-muted border-border'
                  : 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                }`}>{action === 'HOLD' ? 'HOLD — don’t add' : action === 'TRIM' ? 'TRIM' : idea.direction}</span>
                <PortfolioFitBadge fit={idea.portfolio_fit} compact title={idea.position_note ?? undefined} />
              </div>
              <span className={`text-xs font-bold ${(idea.conviction ?? 0) >= 70 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {fmtPct(idea.conviction, 0)}
              </span>
            </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function RiskRadarWidget() {
  const [risks, setRisks] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/api/risk-radar')
      .then(r => setRisks((r.data?.risks ?? []).slice(0, 4)))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-rose" aria-hidden />
          <h2 className="panel-title">Risk Radar</h2>
        </div>
        <Link to="/app/risk-radar" className="text-xs text-blue-light hover:underline flex items-center gap-1">
          Full view <ArrowRight size={12} />
        </Link>
      </div>
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-8 rounded bg-surface-hover/40 animate-pulse" />)}</div>
      ) : risks.length === 0 ? (
        <div className="flex items-center gap-2 py-4 text-center justify-center">
          <Shield size={20} className="text-emerald-400" />
          <p className="text-sm text-emerald-400 font-semibold">All Clear</p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {risks.map((r: any, i: number) => (
            <li key={i} className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
              <AlertTriangle size={12} className={`mt-0.5 ${r.severity === 'high' ? 'text-rose-400' : 'text-amber-400'}`} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-text-primary truncate">{r.title}</p>
                {r.ticker && <Link to={`/app/stock/${r.ticker}`} className="text-[11px] text-blue-light hover:underline">{r.ticker}</Link>}
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${
                r.severity === 'high' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
              }`}>{r.severity}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function TopSignalsWidget() {
  const [signals, setSignals] = useState<DigestSignal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const navigate = useNavigate()

  const load = useCallback(() => {
    setLoading(true)
    setError(false)
    api.get('/api/signals/digest?limit=5')
      // Macro/regime rows carry no ticker — drop them so a chip can never
      // navigate to /app/stock/null (same rule as LiveSignalsFeed).
      .then(r => setSignals(((r.data?.signals ?? r.data ?? []) as DigestSignal[])
        .filter(s => s.ticker && String(s.ticker).length > 0)))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-amber" aria-hidden />
          <h2 className="panel-title">Top Signals</h2>
        </div>
        <Link to="/app/signals" className="text-xs text-blue-light hover:underline flex items-center gap-1">
          All signals <ArrowRight size={12} />
        </Link>
      </div>

      {loading ? (
        <ul className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <li key={i} className="h-10 rounded-lg bg-surface-hover/40 animate-pulse" />
          ))}
        </ul>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <p className="text-sm text-text-muted">Could not load signals</p>
          <button type="button" onClick={load} className="text-xs text-blue-light hover:underline flex items-center gap-1">
            <RefreshCw size={11} aria-hidden /> Try again
          </button>
        </div>
      ) : signals.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
          <Zap size={28} className="text-text-muted opacity-30" aria-hidden />
          <p className="text-sm text-text-muted">No signals available</p>
        </div>
      ) : (
        <ul className="space-y-2" aria-label="Top scored signals">
          {signals.map((sig, i) => (
            <li key={i} className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
              <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${
                sig.sentiment > 0 ? 'bg-emerald-500/20 text-emerald-400'
                : sig.sentiment < 0 ? 'bg-rose-500/20 text-rose-400'
                : 'bg-surface-hover text-text-muted'
              }`}>
                {sig.source}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => { if (sig.ticker) navigate(`/app/stock/${encodeURIComponent(sig.ticker)}`) }}
                    className="font-mono text-xs font-bold text-text-primary hover:text-blue-light transition-colors"
                  >
                    {sig.ticker}
                  </button>
                  {sig.score != null && (
                    <span className="text-[11px] font-mono text-text-muted">score {fmtNum(sig.score, 1)}</span>
                  )}
                </div>
                <p className="text-xs text-text-muted truncate">{sig.summary}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Convergences mini-widget ─────────────────────────────────────────────────

interface ConvergenceEntry {
  ticker: string
  /** Backend emits `direction`: 'bullish' | 'bearish' | 'mixed'. */
  direction: string
  source_count: number
  net_sentiment: number
}

function ConvergencesWidget() {
  const [entries, setEntries] = useState<ConvergenceEntry[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    // No `limit` param server-side — cap client-side to keep the widget compact.
    api.get('/api/signals/convergences')
      .then(r => setEntries(((r.data?.convergences ?? r.data ?? []) as ConvergenceEntry[]).slice(0, 8)))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={16} className="text-purple-light" aria-hidden />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.confluence}>Convergences</h2>
        </div>
        <Link to="/app/signals" className="text-xs text-blue-light hover:underline flex items-center gap-1">
          View all <ArrowRight size={12} />
        </Link>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-8 rounded bg-surface-hover/40 animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
          <Target size={28} className="text-text-muted opacity-30" aria-hidden />
          <p className="text-sm text-text-muted">No multi-source convergences detected</p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {entries.map(e => {
            const color =
              e.direction === 'bullish' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
              : e.direction === 'bearish' ? 'border-rose-500/40 bg-rose-500/10 text-rose-400'
              : 'border-border bg-surface-hover text-text-muted'
            return (
              <button
                key={e.ticker}
                type="button"
                onClick={() => navigate(`/app/stock/${e.ticker}`)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-mono font-semibold transition-colors hover:opacity-80 ${color}`}
                title={`${e.source_count} independent sources pointing the same way on ${e.ticker}`}
              >
                {e.ticker}
                <span className="text-[11px] font-normal opacity-70">{e.source_count}src</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Multi-Category Convergences widget ────────────────────────────────────
// Highest-quality setups: 4+ distinct origination categories (regulatory,
// technical, macro, sentiment, options, political, fundamentals) all
// agreeing on direction. Much rarer than raw source-count convergence
// which is why this gets its own prominent surface.
interface MultiCatEntry {
  ticker: string
  direction: 'LONG' | 'SHORT' | 'WATCH'
  avg_sentiment: number
  avg_confidence: number
  category_count: number
  categories: Record<string, string[]>
  source_count: number
  signal_count: number
}

function MultiCategoryWidget() {
  const [entries, setEntries] = useState<MultiCatEntry[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/multi-category/live?min_categories=3&lookback_hours=24&limit=10')
      .then(r => setEntries(r.data?.convergences ?? []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [])

  const badgeFor = (direction: string, catCount: number) => {
    if (catCount >= 5) return 'border-fuchsia-500/40 bg-fuchsia-500/15 text-fuchsia-400'
    if (direction === 'LONG') return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
    if (direction === 'SHORT') return 'border-rose-500/40 bg-rose-500/10 text-rose-400'
    return 'border-border bg-surface-hover text-text-secondary'
  }

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={16} className="text-fuchsia-400" aria-hidden />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.multiCat}>🔥 Multi-Category Convergence</h2>
        </div>
        <Link to="/app/trade-ideas" className="text-xs text-blue-light hover:underline flex items-center gap-1">
          Act on ideas <ArrowRight size={12} />
        </Link>
      </div>
      <p className="text-[11px] text-text-muted mb-3">
        Names where 3+ distinct signal <em>categories</em> agree. Regulatory + technical + sentiment + macro stacking together is Citadel-grade setup.
      </p>

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-10 rounded bg-surface-hover/40 animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
          <Target size={28} className="text-text-muted opacity-30" aria-hidden />
          <p className="text-sm text-text-muted">No multi-category convergences right now</p>
          <p className="text-[11px] text-text-muted">Check back — these are rare setups</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(e => (
            <button
              key={e.ticker}
              type="button"
              onClick={() => navigate(`/app/stock/${e.ticker}`)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left transition hover:opacity-90 ${badgeFor(e.direction, e.category_count)}`}
              title={Object.entries(e.categories)
                .map(([c, srcs]) => `${c}: ${srcs.join(', ')}`)
                .join(' · ')}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold">{e.ticker}</span>
                <span className="text-[11px] uppercase tracking-wider opacity-80">{e.direction}</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] font-mono opacity-90">
                {Object.keys(e.categories).map(c => (
                  <span key={c} className="px-1.5 py-0.5 rounded bg-black/30">{c}</span>
                ))}
                <span className="opacity-70 ml-1">{e.category_count}×</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Recent Alerts feed — unified stream ──────────────────────────────────
// One scrollable list of every alert firing (convergence + perfect storm)
// ordered newest-first. Replaces scrolling Discord to see what's landed.
interface AlertEntry {
  type: 'convergence' | 'perfect_storm' | 'trend_shift'
  ticker: string
  direction: string
  fired_at: string | null
  summary: string
}

function RecentAlertsWidget() {
  const [alerts, setAlerts] = useState<AlertEntry[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/alert-feed?limit=15')
      .then(r => setAlerts(r.data?.alerts ?? []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (alerts.length === 0) return null

  const typeIcon = (t: string) =>
    t === 'perfect_storm' ? '🌋' :
    t === 'convergence' ? '🌐' :
    t === 'trend_shift' ? '⚡' : '•'

  const typeColor = (t: string) =>
    t === 'perfect_storm' ? 'border-fuchsia-500/30 bg-fuchsia-500/5'
    : t === 'trend_shift' ? 'border-amber-500/30 bg-amber-500/5'
    : 'border-blue-500/30 bg-blue-500/5'

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-indigo-400" aria-hidden />
          <h2 className="panel-title">Recent Alerts</h2>
          <span className="text-[11px] text-text-muted cursor-help" title="🌋 perfect storm · 🌐 multi-source convergence · ⚡ trend shift">unified feed · newest first</span>
        </div>
      </div>
      <div className="space-y-1 max-h-[280px] overflow-auto">
        {alerts.map((a, i) => (
          <button
            key={`${a.type}-${a.ticker}-${a.fired_at}-${i}`}
            type="button"
            onClick={() => navigate(`/app/stock/${encodeURIComponent(a.ticker)}?tab=Signals`)}
            className={clsx(
              'w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded border text-xs hover:opacity-90 transition',
              typeColor(a.type),
            )}
          >
            <div className="flex items-center gap-2">
              <span>{typeIcon(a.type)}</span>
              <span className="font-mono font-bold">{a.ticker}</span>
              <span className="text-[11px] uppercase opacity-80">{a.direction}</span>
              <span className="text-text-muted text-[11px]">{a.summary}</span>
            </div>
            <span className="text-[11px] text-text-muted font-mono">
              {a.fired_at ? new Date(a.fired_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Week Highlights — positive-reinforcement 7d summary ─────────────────
interface WeekHighlights {
  closes: number
  wins: number
  losses: number
  win_rate_pct: number
  realised_gbp: number
  best: { ticker: string; direction: string; pnl_gbp: number } | null
  worst: { ticker: string; direction: string; pnl_gbp: number } | null
}

function WeekHighlightsWidget() {
  const [data, setData] = useState<WeekHighlights | null>(null)

  useEffect(() => {
    api.get('/api/blotter/week-highlights')
      .then(r => setData(r.data ?? null))
      .catch(() => setData(null))
  }, [])

  if (!data || data.closes === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="panel-title">Your Week · last 7 days</p>
          <p className="text-lg font-bold mt-1">
            <span className="text-text-primary">{data.closes} closes · {data.wins}W / {data.losses}L</span>
            <span className="text-text-muted cursor-help" title={JARGON_HELP.winRate}> · {fmtPct(data.win_rate_pct, 0)} win rate</span>
          </p>
        </div>
        <div className={clsx(
          'text-2xl font-bold font-mono',
          data.realised_gbp >= 0 ? 'text-emerald-400' : 'text-rose-400',
        )}>
          {data.realised_gbp >= 0 ? '+' : ''}£{Math.round(data.realised_gbp).toLocaleString('en-GB')}
        </div>
      </div>
      {(data.best || data.worst) && (
        <div className="mt-3 flex flex-wrap gap-3 text-xs border-t border-border/40 pt-2">
          {data.best && (
            <div>
              <span className="text-text-muted">🏆 Best:</span>{' '}
              <span className="font-mono font-bold text-emerald-400">{data.best.ticker}</span>{' '}
              <span className="text-emerald-400">+£{Math.round(data.best.pnl_gbp).toLocaleString('en-GB')}</span>
            </div>
          )}
          {data.worst && (
            <div>
              <span className="text-text-muted">💀 Worst:</span>{' '}
              <span className="font-mono font-bold text-rose-400">{data.worst.ticker}</span>{' '}
              <span className="text-rose-400">£{Math.round(data.worst.pnl_gbp).toLocaleString('en-GB')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Observations — auto-generated nudges ────────────────────────────────
interface Observation {
  kind: string
  icon: string
  text: string
}

function ObservationsWidget() {
  const [items, setItems] = useState<Observation[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    api.get('/api/observations')
      .then(r => setItems(r.data?.observations ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true))
  }, [])

  if (!loaded || items.length === 0) return null

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-2 flex items-center gap-2">
        <span>💡</span> Observations
      </h2>
      <div className="space-y-1">
        {items.map((o, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-text-secondary">
            <span className="shrink-0">{o.icon}</span>
            <span>{o.text}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Action Now — top 5 ranked items to act on ──────────────────────────
interface ActionItem {
  rank: number
  urgency: 'HIGH' | 'MEDIUM' | 'LOW'
  kind: 'at_entry' | 'multi_cat' | 'holdings_risk'
  ticker: string
  direction: string
  reason: string
  deep_link: string
  score: number
  portfolio_fit?: PortfolioFit | null
}

function ActionNowWidget() {
  const [actions, setActions] = useState<ActionItem[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/signals/action-now')
      .then(r => setActions(r.data?.actions ?? []))
      .catch(() => setActions([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (actions.length === 0) return null

  const tint = (k: string, urgency: string) => {
    if (k === 'at_entry') return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
    if (k === 'holdings_risk') return 'border-rose-500/40 bg-rose-500/10 text-rose-200'
    if (urgency === 'HIGH') return 'border-amber-500/40 bg-amber-500/10 text-amber-200'
    return 'border-blue-500/40 bg-blue-500/10 text-blue-200'
  }

  const kindLabel = (k: string) => k === 'at_entry' ? '🎯 at entry'
    : k === 'multi_cat' ? '🔥 multi-cat'
    : k === 'holdings_risk' ? '⚠️ holdings risk'
    : k

  return (
    <div className="card p-4 border border-amber-500/30 bg-amber-500/5">
      <h2 className="panel-title flex items-center gap-2 mb-3">
        <span className="text-xl">⚡</span>
        Action Now · Top {actions.length}
      </h2>
      <div className="space-y-1.5">
        {actions.map(a => (
          <button
            key={`${a.kind}-${a.ticker}`}
            type="button"
            onClick={() => { if (a.deep_link) navigate(a.deep_link) }}
            className={clsx('w-full flex items-start gap-3 px-3 py-2 rounded border hover:opacity-90 transition text-left text-xs', tint(a.kind, a.urgency))}
          >
            <span className="font-mono font-bold opacity-70 w-6">#{a.rank}</span>
            <span className="font-mono font-bold w-16">{a.ticker}</span>
            <span className="text-[11px] uppercase w-14 opacity-80">{a.direction}</span>
            {a.portfolio_fit && <PortfolioFitBadge fit={a.portfolio_fit} compact />}
            <span
              className="text-[11px] uppercase w-24 opacity-80 cursor-help"
              title={a.kind === 'at_entry'
                ? 'The price is at the planned entry level — the plan says act now'
                : a.kind === 'multi_cat'
                ? JARGON_HELP.multiCat
                : 'Bearish signal flow against a position you hold'}
            >{kindLabel(a.kind)}</span>
            <span className="flex-1 opacity-90">{a.reason}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Allocation coherence — portfolio-level view of today's bullish signals ─
interface CoherenceBucket {
  sector: string
  current_weight_pct: number
  signal_count: number
  addable_count: number
  verdict: 'conflict' | 'caution' | 'ok' | 'neutral'
  note: string
  tickers: string[]
}
interface AllocationCoherenceResponse {
  has_book: boolean
  book_total_gbp: number
  buckets: CoherenceBucket[]
  summary: string[]
}

const COHERENCE_CHIP: Record<CoherenceBucket['verdict'], string> = {
  conflict: 'border-rose-500/40 bg-rose-500/15 text-rose-400',
  caution: 'border-amber-500/40 bg-amber-500/15 text-amber-400',
  ok: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-400',
  neutral: 'border-border bg-surface-hover text-text-secondary',
}

function AllocationCoherenceWidget() {
  const [data, setData] = useState<AllocationCoherenceResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get('/api/signals/allocation-coherence')
      .then(r => setData(r.data ?? null))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading || !data || !data.has_book) return null

  const active = (data.buckets ?? []).filter(b => b.signal_count > 0)
  if (active.length === 0 && (data.summary ?? []).length === 0) return null

  return (
    <div className="card p-4">
      <h2 className="panel-title flex items-center gap-2 mb-3 cursor-help" title={JARGON_HELP.coherence}>
        <span className="text-xl">🧭</span>
        Allocation coherence
      </h2>
      {(data.summary ?? []).length > 0 && (
        <div className="space-y-1.5 mb-3">
          {data.summary.map((line, i) => (
            <p key={i} className="text-sm text-text-primary leading-snug">{line}</p>
          ))}
        </div>
      )}
      {active.length === 0 ? (
        <p className="text-xs text-text-muted">No convergent signals in your sectors right now.</p>
      ) : (
        <div className="space-y-1.5">
          {active.map(b => (
            <div key={b.sector} className="flex items-center gap-3 text-xs">
              <span className="flex-1 min-w-0 truncate text-text-secondary">{b.sector}</span>
              <span className="font-mono text-text-muted w-14 text-right">{fmtPct(b.current_weight_pct, 1)}</span>
              <span className="font-mono text-text-muted w-20 text-right">
                {b.signal_count} signal{b.signal_count === 1 ? '' : 's'}
              </span>
              <span
                className={clsx('rounded border px-1.5 py-0.5 text-[11px] font-semibold uppercase w-20 text-center', COHERENCE_CHIP[b.verdict])}
                title={b.note || undefined}
              >
                {b.verdict}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Holdings News — latest signal summaries per held ticker ─────────────
interface NewsItem {
  ticker: string
  source: string
  signal_type: string
  sentiment: number
  summary: string
  created_at: string
}

function HoldingsNewsWidget() {
  const [items, setItems] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/signals/holdings-news?hours=24')
      .then(r => setItems(r.data?.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (items.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-blue-light" />
          <h2 className="panel-title">Holdings News · 24h</h2>
        </div>
        <span className="text-[11px] text-text-muted">most-recent signal per ticker</span>
      </div>
      <div className="space-y-1 max-h-[260px] overflow-auto">
        {items.slice(0, 20).map(item => {
          const sentTint = item.sentiment > 0.15 ? 'text-emerald-400' : item.sentiment < -0.15 ? 'text-rose-400' : 'text-text-secondary'
          return (
            <button
              key={`${item.ticker}-${item.created_at}`}
              type="button"
              onClick={() => navigate(`/app/stock/${encodeURIComponent(item.ticker)}?tab=Signals`)}
              className="w-full flex items-start gap-2 px-3 py-1.5 rounded border border-border/40 hover:bg-surface-hover/30 text-left"
            >
              <span className={clsx('font-mono font-bold text-xs shrink-0', sentTint)}>{item.ticker}</span>
              <span className="text-[11px] text-text-muted font-mono shrink-0">{item.source}</span>
              <span className="text-[11px] text-text-secondary line-clamp-1 flex-1">{item.summary}</span>
              <span className="text-[10px] text-text-muted shrink-0">
                {item.created_at ? new Date(item.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Market Session Indicator — know what's open right now ────────────────
function MarketSessionBadge() {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(iv)
  }, [])

  const uk = now.toLocaleString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', weekday: 'short' })

  // DST-safe: compute each exchange's LOCAL clock via Intl.DateTimeFormat
  // (same approach as getMarketSession) instead of fixed UTC offsets, which
  // are an hour wrong for half the year in London and New York.
  const zoneClock = (timeZone: string) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone, hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
    }).formatToParts(now)
    const get = (t: string) => parts.find(p => p.type === t)?.value ?? ''
    const mins = (Number(get('hour')) % 24) * 60 + Number(get('minute'))
    const weekend = get('weekday') === 'Sat' || get('weekday') === 'Sun'
    return { mins: Number.isFinite(mins) ? mins : 0, weekend }
  }
  const ldn = zoneClock('Europe/London')
  const ny = zoneClock('America/New_York')

  // Exchange-local session windows: LSE 08:00–16:30, NYSE 09:30–16:00,
  // US pre-market 04:00–09:30, US after-hours 16:00–20:00.
  const lseOpen = !ldn.weekend && ldn.mins >= 8 * 60 && ldn.mins < 16 * 60 + 30
  const nyseOpen = !ny.weekend && ny.mins >= 9 * 60 + 30 && ny.mins < 16 * 60
  const preMarketUS = !ny.weekend && ny.mins >= 4 * 60 && ny.mins < 9 * 60 + 30
  const afterHoursUS = !ny.weekend && ny.mins >= 16 * 60 && ny.mins < 20 * 60

  // Countdown helpers — minutes until an exchange-local target time
  const minutesUntil = (nowMins: number, targetMins: number) => {
    let d = targetMins - nowMins
    if (d <= 0) d += 24 * 60
    return d
  }
  const fmtCountdown = (mins: number) => {
    if (mins < 60) return `${mins}m`
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return m === 0 ? `${h}h` : `${h}h ${m}m`
  }

  // Next relevant event — each countdown measured on that exchange's own clock
  let next_event = ''
  if (!lseOpen) next_event = `LSE opens in ${fmtCountdown(minutesUntil(ldn.mins, 8 * 60))}`
  else if (!nyseOpen) next_event = `NYSE opens in ${fmtCountdown(minutesUntil(ny.mins, 9 * 60 + 30))}`
  else if (ldn.mins < 16 * 60 + 30) next_event = `LSE closes in ${fmtCountdown(minutesUntil(ldn.mins, 16 * 60 + 30))}`
  else next_event = `NYSE closes in ${fmtCountdown(minutesUntil(ny.mins, 16 * 60))}`

  const sessions = [
    {
      name: 'LSE',
      state: lseOpen ? 'OPEN' : 'CLOSED',
      tint: lseOpen ? 'text-emerald-400' : 'text-text-muted',
    },
    {
      name: 'NYSE',
      state: nyseOpen ? 'OPEN' : preMarketUS ? 'PRE-MKT' : afterHoursUS ? 'POST-MKT' : 'CLOSED',
      tint: nyseOpen ? 'text-emerald-400' : preMarketUS ? 'text-amber-400' : afterHoursUS ? 'text-blue-400' : 'text-text-muted',
    },
  ]

  return (
    <div className="flex items-center gap-3 text-[11px] text-text-muted font-mono flex-wrap">
      <span>🕒 {uk} UK</span>
      {sessions.map(s => (
        <span key={s.name} className="flex items-center gap-1">
          <span>{s.name}</span>
          <span className={clsx('font-semibold', s.tint)}>{s.state}</span>
        </span>
      ))}
      <span className="text-text-muted">→ {next_event}</span>
    </div>
  )
}

// ── Next Held-Position Earnings — binary-event calendar ─────────────────
function NextEarningsBanner() {
  const [data, setData] = useState<{ ticker: string; date: string; days_away: number } | null>(null)

  useEffect(() => {
    api.get('/api/signals/next-held-earnings')
      .then(r => setData(r.data?.next ?? null))
      .catch(() => setData(null))
  }, [])

  if (!data) return null
  const isUrgent = data.days_away <= 3
  return (
    <div className={clsx(
      'rounded-lg border px-3 py-2 flex items-center gap-3 text-xs',
      isUrgent ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
        : 'border-amber-500/40 bg-amber-500/10 text-amber-200',
    )}>
      <span>📣</span>
      <span className="font-mono font-semibold">{data.ticker}</span>
      <span>earnings</span>
      <span className="font-mono">
        {data.days_away === 0 ? 'TODAY' : data.days_away === 1 ? 'tomorrow' : `in ${data.days_away} days`}
      </span>
      <span className="text-text-muted">({data.date})</span>
    </div>
  )
}

// ── Top Heat — hottest tickers right now ─────────────────────────────────
interface HeatEntry {
  ticker: string
  heat_score: number
  n_sources: number
  n_signals: number
  avg_sentiment: number
  direction: 'LONG' | 'SHORT' | 'MIXED'
  sources: string[]
}

function TopHeatWidget() {
  const [entries, setEntries] = useState<HeatEntry[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/signals/top-heat?limit=10')
      .then(r => setEntries(r.data?.tickers ?? []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (entries.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">🔥</span>
          <h2 className="panel-title cursor-help" title={JARGON_HELP.heat}>Top Heat · 24h</h2>
        </div>
        <span className="text-[11px] text-text-muted cursor-help" title={JARGON_HELP.sentiment}>source × signals × |sentiment|</span>
      </div>
      <div className="space-y-1">
        {entries.map((e, i) => {
          const tint = e.direction === 'LONG' ? 'text-emerald-400'
            : e.direction === 'SHORT' ? 'text-rose-400'
            : 'text-amber-400'
          return (
            <div
              key={e.ticker}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded border border-border/50 hover:bg-surface-hover/30 text-xs"
              title={`${e.n_sources} sources · ${e.n_signals} signals · avg sent ${fmtNum(e.avg_sentiment)} · ${e.sources.join(', ')}`}
            >
              <button
                type="button"
                onClick={() => navigate(`/app/stock/${encodeURIComponent(e.ticker)}?tab=Signals`)}
                className="flex items-center gap-2 min-w-0"
              >
                <span className="text-text-muted w-5 text-right">#{i + 1}</span>
                <span className={clsx('font-mono font-bold', tint)}>{e.ticker}</span>
                <span className={clsx('text-[11px] uppercase', tint)}>{e.direction}</span>
                <span className="text-[11px] text-text-muted">{e.n_sources}src · {e.n_signals}sig</span>
              </button>
              <div className="flex items-center gap-2">
                <span className={clsx('font-mono font-semibold', tint)}>{fmtNum(e.heat_score, 0)}</span>
                <Link
                  to={`/app/chart?symbol=${encodeURIComponent(e.ticker)}`}
                  onClick={ev => ev.stopPropagation()}
                  title="Open signal-aware chart (signals · SMC · trend)"
                  className="text-text-muted hover:text-blue-light"
                >
                  📈
                </Link>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Concentration warnings — single-position & sector risk ──────────────
interface ConcentrationWarning {
  kind: 'single_position' | 'sector_concentration'
  ticker?: string
  sector?: string
  pct_of_book: number
  value_gbp: number
  severity: 'high' | 'medium'
  message: string
}

function ConcentrationWidget() {
  const [warnings, setWarnings] = useState<ConcentrationWarning[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    api.get('/api/concentration/check')
      .then(r => setWarnings(r.data?.warnings ?? []))
      .catch(() => setWarnings([]))
      .finally(() => setLoaded(true))
  }, [])

  if (!loaded || warnings.length === 0) return null

  return (
    <div className="card p-4 border border-amber-500/30 bg-amber-500/5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">⚖️</span>
        <h2 className="panel-title">Concentration Warnings</h2>
      </div>
      <div className="space-y-1.5">
        {warnings.map((w, i) => (
          <div
            key={i}
            className={clsx(
              'flex items-center justify-between gap-2 px-3 py-1.5 rounded border text-xs',
              w.severity === 'high' ? 'border-rose-500/40 bg-rose-500/5 text-rose-200'
              : 'border-amber-500/40 bg-amber-500/5 text-amber-200',
            )}
          >
            <span>{w.message}</span>
            <span className="font-mono text-[11px]">£{w.value_gbp.toLocaleString('en-GB')}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Conviction Histogram — pipeline depth at a glance ────────────────────
function ConvictionHistogramWidget() {
  const [bins, setBins] = useState<number[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get('/api/signals/conviction-histogram')
      .then(r => {
        setBins(r.data?.bins ?? [])
        setTotal(r.data?.total ?? 0)
      })
      .catch(() => setBins([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (bins.length === 0 || total === 0) return null
  const maxN = Math.max(...bins, 1)

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={14} className="text-blue-light" />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.conviction}>Conviction Distribution</h2>
          <span className="text-[11px] text-text-muted">{total} ideas · 48h window</span>
        </div>
      </div>
      <div className="flex items-end gap-1 h-16">
        {bins.map((n, i) => {
          const pct = (n / maxN) * 100
          const tint = i >= 7 ? 'bg-emerald-500/40' : i >= 5 ? 'bg-amber-500/40' : 'bg-surface-hover'
          return (
            <div
              key={i}
              className={clsx('flex-1 rounded-t transition relative', tint)}
              style={{ height: `${pct}%` }}
              title={`${i * 10}–${(i + 1) * 10} conviction: ${n} ideas`}
            >
              {n > 0 && <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] text-text-muted">{n}</span>}
            </div>
          )
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-text-muted">
        <span>0</span>
        <span>conviction →</span>
        <span>100</span>
      </div>
    </div>
  )
}

// ── Signal Velocity — hourly signal production ────────────────────────────
// Inline SVG stacked bar chart of signals-per-hour over last 24h. Lets
// Simon see at a glance whether the pipe is producing steadily or in
// bursts — and quickly spot outages.
interface VelocityHour {
  hour: string
  n: number
  n_sources: number
  n_tickers: number
  avg_sentiment: number
}

function SignalVelocityWidget() {
  const [hours, setHours] = useState<VelocityHour[]>([])
  const [total, setTotal] = useState(0)
  const [avgPerHour, setAvgPerHour] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get('/api/signal-pipe/hourly')
      .then(r => {
        setHours(r.data?.hours ?? [])
        setTotal(r.data?.total_24h ?? 0)
        setAvgPerHour(r.data?.avg_per_hour ?? 0)
      })
      .catch(() => setHours([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (hours.length === 0) return null

  const maxN = Math.max(...hours.map(h => h.n), 1)

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-cyan-400" aria-hidden />
          <h2 className="panel-title">Signal Velocity</h2>
          <span className="text-[11px] text-text-muted">{total.toLocaleString()} signals · 24h · avg {avgPerHour}/h</span>
        </div>
      </div>
      <div className="flex items-end gap-0.5 h-20">
        {hours.map(h => {
          const pct = (h.n / maxN) * 100
          const tint = h.avg_sentiment > 0.15 ? 'bg-emerald-500/50'
            : h.avg_sentiment < -0.15 ? 'bg-rose-500/50'
            : 'bg-cyan-500/40'
          return (
            <div
              key={h.hour}
              className={clsx('flex-1 rounded-t transition', tint)}
              style={{ height: `${pct}%` }}
              title={`${h.hour.slice(11, 16)} — ${h.n} signals · ${h.n_sources} sources · ${h.n_tickers} tickers · sent ${fmtNum(h.avg_sentiment)}`}
            />
          )
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-text-muted">
        <span>{hours[0]?.hour.slice(11, 16)}</span>
        <span>{hours[hours.length - 1]?.hour.slice(11, 16)}</span>
      </div>
    </div>
  )
}

// ── Perfect Storm history feed ────────────────────────────────────────────
// Last ~20 times all three filters aligned (multi-cat + regime + entry zone)
// on a saved idea. Lets Simon retrospectively review whether acting on them
// would have worked. Each row links to the ticker.
interface PerfectStormEntry {
  ticker: string
  direction: 'LONG' | 'SHORT'
  entry: number
  current: number
  category_count: number
  categories: string[]
  regime_score: number
  fired_at: string
}

function PerfectStormWidget() {
  const [history, setHistory] = useState<PerfectStormEntry[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/multi-category/perfect-storms/recent?limit=10')
      .then(r => setHistory(r.data?.history ?? []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (history.length === 0) return null

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">🌋</span>
          <h2 className="panel-title cursor-help" title={JARGON_HELP.perfectStorm}>Recent Perfect Storms</h2>
        </div>
        <span className="text-[11px] text-text-muted cursor-help" title={`Multi-cat = several distinct signal categories agreeing. ${JARGON_HELP.regime}`}>
          saved + multi-cat + regime-aligned + at entry
        </span>
      </div>
      <div className="space-y-1.5">
        {history.map((h, i) => (
          <button
            key={`${h.ticker}-${h.fired_at}-${i}`}
            type="button"
            onClick={() => navigate(`/app/stock/${encodeURIComponent(h.ticker)}?tab=Signals`)}
            className={clsx(
              'w-full flex items-center justify-between gap-2 px-3 py-2 rounded border transition hover:opacity-90 text-xs',
              h.direction === 'LONG'
                ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-200'
                : 'border-rose-500/30 bg-rose-500/5 text-rose-200',
            )}
            title={`Categories: ${h.categories.join(', ')} · Regime: ${fmtNum(h.regime_score)}`}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold">{h.ticker}</span>
              <span className="text-[11px] uppercase">{h.direction}</span>
              <span className="text-[11px] text-text-muted">
                {new Date(h.fired_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px] font-mono">
              <span>{fmtGBP(h.entry, 2)} → {fmtGBP(h.current, 2)}</span>
              <span className={
                ((h.current - h.entry) / h.entry * (h.direction === 'LONG' ? 1 : -1)) >= 0
                  ? 'text-emerald-400' : 'text-rose-400'
              }>
                {fmtPct(((h.current - h.entry) / h.entry) * 100 * (h.direction === 'LONG' ? 1 : -1), 2)}
              </span>
              <span className="text-fuchsia-400">{h.category_count}cat</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Holdings Heatmap ──────────────────────────────────────────────────────
// One coloured cell per held ticker: red = bearish signal flow today,
// green = bullish, grey = quiet. At-a-glance read of which parts of the
// book are in danger and which are getting tailwind confirmation.
interface HeatmapCell {
  ticker: string
  avg_sent: number
  signal_count: number
  sources: string[]
}

function HoldingsHeatmap() {
  const [cells, setCells] = useState<HeatmapCell[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/signals/holdings-heatmap')
      .then(r => setCells(r.data?.cells ?? []))
      .catch(() => setCells([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="card p-4 h-32 animate-pulse bg-surface-hover/20" />
  if (cells.length === 0) return null

  // Net book-level sentiment summary (for the header pill)
  const withSignals = cells.filter(c => c.signal_count > 0)
  const bull = withSignals.filter(c => c.avg_sent > 0.1).length
  const bear = withSignals.filter(c => c.avg_sent < -0.1).length
  const mixed = withSignals.length - bull - bear
  const quiet = cells.length - withSignals.length
  const avgBook = withSignals.length > 0
    ? withSignals.reduce((s, c) => s + c.avg_sent, 0) / withSignals.length
    : 0
  const skewColor = avgBook >= 0.1 ? 'text-emerald-400' : avgBook <= -0.1 ? 'text-rose-400' : 'text-text-secondary'

  const colorFor = (c: HeatmapCell) => {
    if (c.signal_count === 0) return 'bg-surface-hover/60 text-text-muted border-border'
    const s = c.avg_sent
    if (s >= 0.5) return 'bg-emerald-500/30 border-emerald-500/50 text-emerald-100'
    if (s >= 0.2) return 'bg-emerald-500/20 border-emerald-500/30 text-emerald-200'
    if (s >= 0.05) return 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
    if (s <= -0.5) return 'bg-rose-500/30 border-rose-500/50 text-rose-100'
    if (s <= -0.2) return 'bg-rose-500/20 border-rose-500/30 text-rose-200'
    if (s <= -0.05) return 'bg-rose-500/10 border-rose-500/20 text-rose-400'
    return 'bg-surface-hover border-border text-text-secondary'
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Target size={16} className="text-blue-light" aria-hidden />
          <h2 className="panel-title">Holdings Heatmap</h2>
          <span className={clsx('text-xs font-mono', skewColor)} title={`Avg sentiment across held tickers with signals: ${fmtNum(avgBook, 3)}`}>
            book {fmtSigned(avgBook)}
          </span>
          <span className="text-[11px] text-text-muted">
            ↑{bull} ↓{bear} ≈{mixed} ○{quiet}
          </span>
        </div>
        <span className="text-[11px] text-text-muted cursor-help" title={JARGON_HELP.sentiment}>24h sentiment · red = risk · green = tailwind</span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-1.5">
        {cells.map(c => (
          <button
            key={c.ticker}
            type="button"
            onClick={() => navigate(`/app/stock/${encodeURIComponent(c.ticker)}?tab=Signals`)}
            className={clsx(
              'text-xs px-2 py-1.5 rounded border font-mono transition hover:opacity-80',
              colorFor(c),
            )}
            title={c.signal_count === 0
              ? `${c.ticker}: no signals in 24h`
              : `${c.ticker}: avg sent ${fmtNum(c.avg_sent)} · ${c.signal_count} signals · ${c.sources.slice(0, 5).join(', ')}`}
          >
            <div className="font-bold truncate">{c.ticker}</div>
            <div className="text-[10px] opacity-80">
              {c.signal_count === 0 ? '—' : fmtSigned(c.avg_sent)}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Holdings Health Ranking ─────────────────────────────────────────────
// Prioritised list: worst held tickers first (consider trimming), best
// last (tailwind). Pairs with the heatmap grid — where heatmap is a
// colour-wash, this is an action-ranked list.
interface HealthRow {
  ticker: string
  avg_sentiment_24h: number
  signal_count_24h: number
  recent_price_pct: number
  health_score: number | null
}

function HoldingsHealthWidget() {
  const [rows, setRows] = useState<HealthRow[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/signals/holdings-health-ranking')
      .then(r => setRows(r.data?.rankings ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  const scored = rows.filter(r => r.health_score !== null)
  if (scored.length === 0) return null

  // Show 5 worst + 3 best for a focused action view
  const worst = scored.slice(0, 5)
  const best = scored.slice(-3).reverse()

  const tint = (s: number | null) => {
    if (s === null) return 'text-text-muted'
    if (s <= -40) return 'text-rose-400'
    if (s <= -10) return 'text-rose-400/80'
    if (s >= 40) return 'text-emerald-400'
    if (s >= 10) return 'text-emerald-400/80'
    return 'text-text-secondary'
  }

  const Row = ({ r }: { r: HealthRow }) => (
    <button
      type="button"
      onClick={() => navigate(`/app/stock/${encodeURIComponent(r.ticker)}?tab=Signals`)}
      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded border border-border/50 hover:bg-surface-hover/30 text-xs"
    >
      <div className="flex items-center gap-2">
        <span className={clsx('font-mono font-bold', tint(r.health_score))}>{r.ticker}</span>
        <span className="text-[11px] text-text-muted cursor-help" title={JARGON_HELP.sentiment}>
          sent {fmtSigned(r.avg_sentiment_24h)}
        </span>
        <span className="text-[11px] text-text-muted">
          move {fmtPctSigned(r.recent_price_pct, 2)}
        </span>
      </div>
      <span className={clsx('font-mono font-semibold', tint(r.health_score))}>
        {fmtSigned(r.health_score, 0)}
      </span>
    </button>
  )

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={16} className="text-rose-400" aria-hidden />
          <h2 className="panel-title">Holdings Health</h2>
        </div>
        <span className="text-[11px] text-text-muted cursor-help" title={JARGON_HELP.sentiment}>
          composite score: sentiment × 60 + price move
        </span>
      </div>
      {worst.length > 0 && (
        <>
          <p className="text-[11px] text-text-muted mb-1 uppercase tracking-wider">🔻 Consider trimming</p>
          <div className="space-y-1 mb-3">
            {worst.map(r => <Row key={r.ticker} r={r} />)}
          </div>
        </>
      )}
      {best.length > 0 && (
        <>
          <p className="text-[11px] text-text-muted mb-1 uppercase tracking-wider">🚀 Tailwind — hold or add</p>
          <div className="space-y-1">
            {best.map(r => <Row key={r.ticker} r={r} />)}
          </div>
        </>
      )}
    </div>
  )
}

// ── Sector Dispersion Widget ──────────────────────────────────────────────
// Widest-spread sectors first — these are regimes where stock-picking +
// pairs trades work (rotation opportunity). Narrow-spread sectors = trend-
// following regime, just ride the ETF. The table doubles as a pairs-idea
// generator: short the worst name, long the best, hedge out the beta.
interface SectorDisp {
  sector: string
  etf: string
  n_constituents: number
  best_ticker: string
  best_return_pct: number
  worst_ticker: string
  worst_return_pct: number
  spread_pct: number
  median_return_pct: number
  stdev_pct: number
}

function SectorDispersionWidget() {
  const [sectors, setSectors] = useState<SectorDisp[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api.get('/api/sector-dispersion/current')
      .then(r => setSectors(r.data?.sectors ?? []))
      .catch(() => setSectors([]))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={16} className="text-amber-400" aria-hidden />
          <h2 className="panel-title cursor-help" title={JARGON_HELP.dispersion}>Sector Dispersion</h2>
        </div>
        <span className="text-[11px] text-text-muted cursor-help" title={JARGON_HELP.regime}>Wide = rotation regime · Narrow = trend regime</span>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 rounded bg-surface-hover/40 animate-pulse" />
          ))}
        </div>
      ) : sectors.length === 0 ? (
        <div className="py-6 text-center text-sm text-text-muted">No sparkline data yet</div>
      ) : (
        <div className="space-y-1.5">
          {sectors.slice(0, 8).map(s => {
            const spreadWidth = Math.min(100, (s.spread_pct / 5) * 100)  // 5%+ = full bar
            return (
              <div
                key={s.sector}
                className="grid grid-cols-[120px_1fr_auto] items-center gap-2 text-xs"
                title={`Median ${fmtPct(s.median_return_pct, 2)} · σ ${fmtPct(s.stdev_pct, 2)}. ${JARGON_HELP.sigma}`}
              >
                <span className="text-text-primary font-medium truncate">{s.sector}</span>
                <div className="relative h-5 rounded bg-surface-hover/40 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-amber-500/30 border-r border-amber-500/60"
                    style={{ width: `${spreadWidth}%` }}
                  />
                  <div className="relative z-10 flex items-center justify-between h-full px-2 text-[11px] font-mono">
                    <button
                      type="button"
                      onClick={() => navigate(`/app/stock/${s.worst_ticker}`)}
                      className="text-rose-400 hover:underline"
                      title={`${s.worst_ticker}: ${fmtPct(s.worst_return_pct, 2)}`}
                    >
                      {s.worst_ticker} {fmtPct(s.worst_return_pct, 1)}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/app/stock/${s.best_ticker}`)}
                      className="text-emerald-400 hover:underline"
                      title={`${s.best_ticker}: ${fmtPct(s.best_return_pct, 2)}`}
                    >
                      {s.best_ticker} {fmtPct(s.best_return_pct, 1)}
                    </button>
                  </div>
                </div>
                <span className="text-amber-400 font-mono tabular-nums">{fmtPct(s.spread_pct, 1)}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Relative "N seconds/minutes ago" formatter ────────────────────────────────
function formatRelativeTime(date: Date): string {
  const sec = Math.floor((Date.now() - date.getTime()) / 1000)
  if (sec < 5)  return 'just now'
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  return `${Math.floor(min / 60)}h ago`
}

// Book-state strip — one-line summary of in-flight position health
// above the main dashboard widgets. Shows count of acted positions
// still open, how many are underwater > 3%, and multi-cat risk count.
function BookStateStrip() {
  const [state, setState] = useState<{
    saved: number
    acted: number
    acted_down_3pct: number
    near_entry_count: number
    open_pnl_gbp: number
  } | null>(null)
  const [openRisk, setOpenRisk] = useState<{ risk_gbp: number; exposure_gbp: number } | null>(null)

  useEffect(() => {
    api.get('/api/blotter/open-risk')
      .then(r => setOpenRisk({
        risk_gbp: r.data?.total_risk_gbp ?? 0,
        exposure_gbp: r.data?.total_exposure_gbp ?? 0,
      }))
      .catch(() => setOpenRisk(null))
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const snap = await fetchBlotterSnapshot()  // shared with ActiveBlotterWidget
        if (cancelled) return
        if (!snap.ok) { setState(null); return }
        const counts = snap.counts
        const acted = snap.acted
        const saved = snap.saved

        let downCount = 0
        let openPnl = 0
        for (const a of acted) {
          if (a.acted_price && a.current_price) {
            const dir = a.direction === 'SHORT' ? -1 : 1
            const pct = ((a.current_price - a.acted_price) / a.acted_price) * 100 * dir
            if (pct <= -3) downCount++
            if (a.acted_quantity) {
              openPnl += (a.current_price - a.acted_price) * a.acted_quantity * dir
            }
          }
        }
        let nearEntry = 0
        for (const s of saved) {
          if (s.entry_price && s.current_price) {
            const drift = Math.abs(s.current_price - s.entry_price) / s.entry_price * 100
            if (drift <= 3) nearEntry++
          }
        }
        setState({
          saved: counts.saved ?? 0,
          acted: counts.acted ?? 0,
          acted_down_3pct: downCount,
          near_entry_count: nearEntry,
          open_pnl_gbp: openPnl,
        })
      } catch {
        if (!cancelled) setState(null)
      }
    }
    load()
    const iv = setInterval(load, 120_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  if (!state) return null
  if (state.acted === 0 && state.saved === 0) return null

  return (
    <div className="card px-4 py-2 flex items-center gap-4 flex-wrap text-xs">
      <span className="panel-title cursor-help" title={JARGON_HELP.blotter}>Book State</span>
      <Link to="/app/blotter" className="flex items-center gap-1 hover:opacity-80 cursor-help" title="Acted ideas still open — money is in the market">
        <span className="text-blue-light font-bold">{state.acted}</span>
        <span className="text-text-muted">in flight</span>
      </Link>
      {state.acted > 0 && (
        <Link to="/app/blotter" className="flex items-center gap-1 hover:opacity-80" title="Sum of open P&L on acted positions (current_price − acted_price) × qty">
          <span className={state.open_pnl_gbp >= 0 ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>
            {state.open_pnl_gbp >= 0 ? '+' : ''}£{Math.round(state.open_pnl_gbp).toLocaleString('en-GB')}
          </span>
          <span className="text-text-muted">open P&L</span>
        </Link>
      )}
      {openRisk && openRisk.risk_gbp > 0 && (
        <Link to="/app/blotter" className="flex items-center gap-1 hover:opacity-80" title="Sum of |entry − stop| × qty across all acted positions — total £ at risk if every stop hits">
          <span className="text-amber-400 font-bold">£{Math.round(openRisk.risk_gbp).toLocaleString('en-GB')}</span>
          <span className="text-text-muted">at risk</span>
        </Link>
      )}
      {state.acted_down_3pct > 0 && (
        <Link to="/app/blotter" className="flex items-center gap-1 hover:opacity-80" title="Acted positions underwater > 3% vs entry">
          <span className="text-rose-400 font-bold">🔻 {state.acted_down_3pct}</span>
          <span className="text-rose-400">under -3%</span>
        </Link>
      )}
      <Link to="/app/blotter" className="flex items-center gap-1 hover:opacity-80 cursor-help" title="Ideas kept in the blotter but not acted on yet">
        <span className="text-amber-400 font-bold">{state.saved}</span>
        <span className="text-text-muted">saved</span>
      </Link>
      {state.near_entry_count > 0 && (
        <Link to="/app/blotter" className="flex items-center gap-1 hover:opacity-80" title="Saved ideas within 3% of captured entry — ready to act">
          <span className="text-emerald-400 font-bold">🎯 {state.near_entry_count}</span>
          <span className="text-emerald-400">near entry</span>
        </Link>
      )}
      <Link to="/app/daily?tab=alpha-brief" className="ml-auto text-[11px] text-blue-light hover:underline flex items-center gap-1 cursor-help" title={JARGON_HELP.alpha}>
        Alpha Brief <ArrowRight size={10} />
      </Link>
    </div>
  )
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { analysis, analyzing, runAnalysis } = useAgents()
  const { snapshot, history, fetchSnapshot, fetchHistory, loading: portfolioLoading, error: portfolioError } = usePortfolio()
  const rt = useRealtimePortfolio()
  const { summary, accounts, loading: accountsLoading, error: accountsError, fetchSummary, fetchAccounts } = useAccounts()
  const { prefs, toggle: toggleWidget } = useWidgetPrefs()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date())
  const [refreshing, setRefreshing] = useState(false)
  const [relativeTime, setRelativeTime] = useState('just now')

  // Core fetch function — used both on mount and by the auto-refresh interval
  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true)
    await Promise.allSettled([
      fetchSnapshot(),
      fetchHistory(30),
      fetchSummary(),
      fetchAccounts(),
    ])
    const refreshedAt = new Date()
    setLastRefreshed(refreshedAt)
    if (!silent) setRefreshing(false)
  }, [fetchSnapshot, fetchHistory, fetchSummary, fetchAccounts]) // eslint-disable-line

  // Mount: initial load + analysis sweep
  useEffect(() => {
    fetchAll(true)
    runAnalysis().then(() => {
      const { analysis: a } = useAgents.getState()
      if (a) pushAnalysisNotifications(a)
    })
  }, []) // eslint-disable-line

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchAll(true), 60_000)
    return () => clearInterval(interval)
  }, [fetchAll])

  // Update the "last updated" relative string every 5 seconds
  useEffect(() => {
    const tick = setInterval(() => setRelativeTime(formatRelativeTime(lastRefreshed)), 5_000)
    setRelativeTime(formatRelativeTime(lastRefreshed)) // immediate update on new refresh
    return () => clearInterval(tick)
  }, [lastRefreshed])

  const handleManualRefresh = useCallback(async () => {
    await fetchAll(false)
  }, [fetchAll])

  const [marketSession, setMarketSession] = useState(getMarketSession)

  useEffect(() => {
    const interval = setInterval(() => setMarketSession(getMarketSession()), 30_000)
    return () => clearInterval(interval)
  }, [])

  // 30-day NAV series for sparklines on Net Worth + Daily P&L KPI cards.
  // navSeriesLoaded distinguishes "still fetching" (render nothing) from a
  // genuinely empty history (render the honest "history starts today" note —
  // the backend self-heals by writing the first snapshot on that same read).
  const [navSeries, setNavSeries] = useState<Array<{ date: string; nav: number; dailyPnl: number }>>([])
  const [navSeriesLoaded, setNavSeriesLoaded] = useState(false)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/reports/performance-series?days=30')
        const rows = Array.isArray(res.data) ? res.data : (res.data?.series ?? [])
        if (cancelled) return
        const clean = rows
          .filter((r: any) => r && r.portfolio_value != null)
          .map((r: any) => ({
            date: r.date,
            nav: Number(r.portfolio_value ?? 0),
            dailyPnl: Number(r.daily_pnl ?? 0),
          }))
        setNavSeries(clean)
      } catch {
        if (!cancelled) setNavSeries([])
      } finally {
        if (!cancelled) setNavSeriesLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [lastRefreshed])

  // Top proactive AI insight (highest £-impact) for the hero band.
  const [topInsight, setTopInsight] = useState<{ title?: string; impact_gbp?: number; priority?: string } | null>(null)
  useEffect(() => {
    let cancelled = false
    getInsights()
      .then(r => {
        if (cancelled) return
        const items = (r.insights ?? []) as Array<{ title?: string; impact_gbp?: number; priority?: string }>
        const ranked = [...items].sort((a, b) => (b.impact_gbp ?? 0) - (a.impact_gbp ?? 0))
        setTopInsight(ranked[0] ?? null)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Idle cash — same endpoint the Deploy Cash page uses for its hero figure.
  const [idleCash, setIdleCash] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    api.get('/api/deploy-cash/idle')
      .then(r => { if (!cancelled) setIdleCash(r.data?.deployable_gbp ?? null) })
      .catch(() => { if (!cancelled) setIdleCash(null) })
    return () => { cancelled = true }
  }, [])

  const navFirst = navSeries[0]?.nav ?? 0
  const navLast = navSeries[navSeries.length - 1]?.nav ?? 0
  const navMonthChange = navFirst > 0 ? ((navLast - navFirst) / navFirst) * 100 : 0
  const navMonthUp = navMonthChange >= 0
  const pnlMonthSum = navSeries.reduce((s, r) => s + (r.dailyPnl || 0), 0)
  const pnlMonthUp = pnlMonthSum >= 0

  // ── TOTAL NET WORTH: the CANONICAL household figure ─────────────────────────
  // Must match the Net Worth page exactly. The accounts-summary total_net_worth
  // here equals /api/net-worth/summary's total_net_worth_gbp there (same
  // holdings × _holding_value_gbp). We deliberately do NOT lead with rt.value:
  // the realtime websocket value is the TRACKED portfolio only (omits property +
  // cash) and can be a stale reprice — that mismatch is exactly why the two
  // pages disagreed. Realtime only drives the live daily-change below.
  const liveValue   = summary?.total_net_worth ?? snapshot?.total_value ?? rt.value ?? 0
  const liveChange  = rt.dailyChange ?? snapshot?.daily_change ?? 0
  const liveChangePct = rt.dailyChangePct ?? snapshot?.daily_change_pct ?? 0
  const positive    = liveChange >= 0

  // Realtime P&L is only valid when the price cache is active
  const hasPriceCache = rt.value != null

  // ── Chart data: time-series history if available, else per-account bars ─────
  const hasHistory = history.length > 0
  const chartData = hasHistory
    ? history.map(p => ({ date: format(new Date(p.date), 'dd MMM'), value: p.value }))
    : []

  // Build per-account bar data from the accounts store (active accounts only)
  const accountBarData = accounts
    .filter(a => a.is_active)
    .map(a => ({ name: a.name.replace(/\s*\(.*?\)\s*/g, ' ').trim(), value: (a as any).total_value ?? 0 }))
    .filter(a => a.value > 0)
    .sort((a, b) => b.value - a.value)

  // ── REAL-DATA-ONLY: no fabricated headline metrics ──────────────────────────
  // The old "Sharpe" / "Max Drawdown" tiles were permanently empty (their
  // values were hardcoded null after the fabricated formulas were removed).
  // They're replaced by two tiles that always have something real to say:
  // Idle cash (same endpoint the Deploy Cash page uses) and Next Action
  // (the top £-impact insight already fetched for the hero band).

  // ── Win Rate: goals on-track ratio, only when analysis is loaded ─────────────
  const winRate = analysis
    ? `${Math.round(analysis.goals.goals.filter(g => g.on_track).length / Math.max(analysis.goals.goals.length, 1) * 100)}%`
    : null

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Fund header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="page-title">AgentVault Capital</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-sm text-text-secondary">
              {format(new Date(), 'EEEE, d MMMM yyyy')}
            </p>
            <span className="text-xs font-mono flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: marketSession.color }} />
              <span style={{ color: marketSession.color }}>{marketSession.label}</span>
            </span>
          </div>
          <p className="text-xs text-text-muted mt-0.5">
            {analysis
              ? `Last sweep: ${format(new Date(analysis.timestamp), 'HH:mm, d MMM yyyy')}`
              : analyzing
              ? 'Running analysis…'
              : 'No analysis yet — click Refresh'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Last-updated indicator + manual refresh */}
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-text-muted">
            {refreshing && (
              <RefreshCw size={11} className="animate-spin text-blue-light" aria-hidden />
            )}
            <span aria-live="polite" aria-atomic="true">
              {refreshing ? 'Updating…' : `Updated ${relativeTime}`}
            </span>
          </div>
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="btn-ghost p-1.5 rounded-lg disabled:opacity-40"
            aria-label="Refresh dashboard data"
            title="Refresh data (auto-refreshes every 60s)"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin text-blue-light' : ''} aria-hidden />
          </button>
          {/* Widget settings gear */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setSettingsOpen(o => !o)}
              className="btn-ghost p-1.5 rounded-lg"
              aria-label="Customise dashboard sections"
              aria-haspopup="true"
            >
              <Settings size={15} aria-hidden />
            </button>
            {settingsOpen && (
              <WidgetSettingsPanel
                prefs={prefs}
                toggle={toggleWidget}
                onClose={() => setSettingsOpen(false)}
              />
            )}
          </div>
          <button
            type="button"
            onClick={() => runAnalysis()}
            disabled={analyzing}
            className="btn-secondary text-xs gap-1.5"
            aria-label="Refresh analysis"
          >
            <RefreshCw size={13} className={analyzing ? 'animate-spin' : ''} aria-hidden />
            {analyzing ? 'Analysing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Hero band — the at-a-glance "where do we stand" anchor, top of page */}
      <div className="rounded-2xl border border-border/50 bg-gradient-to-br from-surface-card/80 to-surface-DEFAULT p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="panel-title">Net Worth</p>
            <p className="stat-hero font-mono text-text-primary tabular-nums">
              £{Math.round(liveValue).toLocaleString('en-GB')}
            </p>
            {hasPriceCache && (
              <p className="mt-1.5">
                <span className={positive ? 'delta-chip-pos' : 'delta-chip-neg'}>
                  {positive ? '▲' : '▼'} £{Math.abs(Math.round(liveChange)).toLocaleString('en-GB')}
                  {' '}({fmtPctSigned(liveChangePct, 2)}) today
                </span>
              </p>
            )}
          </div>
          {navSeries.length > 1 && (
            <div className="w-full sm:w-64 h-16">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={navSeries} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="heroNav" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={positive ? '#10b981' : '#f43f5e'} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={positive ? '#10b981' : '#f43f5e'} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="nav" stroke={positive ? '#10b981' : '#f43f5e'}
                        strokeWidth={1.5} fill="url(#heroNav)" isAnimationActive={false} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-text-muted text-right -mt-1">30-day trend</p>
            </div>
          )}
          {navSeriesLoaded && navSeries.length <= 1 && (
            <div className="w-full sm:w-64 h-16 flex items-end justify-end">
              <p className="text-[11px] text-text-muted text-right">
                History starts today — first snapshot recorded
              </p>
            </div>
          )}
        </div>
        {/* Top AI insight — highest £-impact action the engine surfaced */}
        {topInsight?.title && (
          <Link to="/app/actions" className="mt-3 flex items-center gap-2 text-xs rounded-lg bg-amber-500/[0.06] border border-amber-500/20 px-3 py-2 hover:bg-amber-500/10 transition-colors">
            <Zap size={13} className="text-amber-400 shrink-0" />
            <span className="text-text-secondary truncate flex-1">{topInsight.title}</span>
            {topInsight.impact_gbp != null && topInsight.impact_gbp > 0 && (
              <span className="font-mono font-semibold text-amber-400 shrink-0">£{Math.round(topInsight.impact_gbp).toLocaleString('en-GB')}</span>
            )}
            <span className="text-[11px] text-text-muted shrink-0 hidden sm:inline">action →</span>
          </Link>
        )}
      </div>

      {/* Geopolitical capital-flight warning — foreign treasury selling vs your
          rate-sensitive/tech exposure (silent unless elevated+). */}
      <TicWarningBanner />

      {/* Front door — "how should I invest my cash?" answer engine */}
      <DeployCashHero />

      {/* LLM morning narrative banner */}
      <MorningNarrativeBanner />

      {/* Macro regime + market session — two compact reads side-by-side on
          wider screens (stacked on mobile) instead of two thin full-width bars. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MacroRegimeBadge />
        <MarketSessionBadge />
      </div>

      {/* Signals Pulse (+ Kelly chip) — pro widget, hidden by default.
          No ShowBar when off: novices shouldn't see a nudge to reveal it;
          opt in via the settings gear ("Pro widgets"). */}
      {prefs.proWidgets && <SignalsPulseBar />}

      {/* Market intelligence — the scrollable strips paired 2-up on wider
          screens (single column on mobile) so they read as one cohesive block
          instead of four full-width bars stacked down the page. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <WhatsMovingStrip />
        <TopTrendsStrip />
        <PredictionMarketsStrip />
        <LatestResearchStrip />
      </div>

      {/* Book State strip — in flight / saved / near-entry / underwater */}
      <BookStateStrip />

      {/* Action Now — top 5 ranked items to act on */}
      <ActionNowWidget />

      {/* Allocation coherence — would acting on today's signals over-concentrate a sector */}
      <AllocationCoherenceWidget />

      {/* Observations — auto-generated nudges */}
      <ObservationsWidget />

      {/* Your Week — 7d closes + best/worst */}
      <WeekHighlightsWidget />

      {/* Concentration warnings — single-position + sector guardrails */}
      <ConcentrationWidget />

      {/* Next earnings across the book — binary-event awareness */}
      <NextEarningsBanner />

      {/* Holdings news feed — latest signal per held ticker */}
      <HoldingsNewsWidget />

      {/* Setup of the Day — highest-conviction current trade idea */}
      <SetupOfTheDay />

      {/* Live Signals Feed — recent ticker-specific signals across feeds */}
      <LiveSignalsFeed />

      {/* Book Signals — what are the feeds saying about YOUR positions */}
      <BookSignalsWidget />

      {/* Top Conviction — highest-ranked tickers OUTSIDE your book */}
      <TopConvictionWidget />

      {/* Active Blotter — ideas you've kept + open positions from blotter */}
      <ActiveBlotterWidget />

      {/* KPI row */}
      {!prefs.kpi ? (
        <ShowBar label={WIDGET_LABELS.kpi} onShow={() => toggleWidget('kpi')} />
      ) : (
      <div className="kpi-grid stagger-children grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Net Worth — total including property + liabilities */}
        <div className="stat-card sm:col-span-2 lg:col-span-1 relative">
          <p className="stat-label">Total Net Worth</p>
          <p className="stat-value text-3xl">{liveValue > 0 ? fmtGBP(liveValue) : '—'}</p>
          {summary && summary.tracked_investments_gbp !== undefined && (
            <p className="text-[11px] text-text-muted mt-1 leading-tight">
              {fmtGBP(summary.tracked_investments_gbp)} tracked
              {summary.property_net_equity_gbp ? ` · ${fmtGBP(summary.property_net_equity_gbp)} property` : ''}
            </p>
          )}
          {summary && (summary.accessible_cash_gbp ?? 0) + (summary.wrapper_cash_gbp ?? 0) > 0 && (
            <p className="text-[11px] text-text-muted mt-0.5 leading-tight" title="Accessible = spendable (bank/savings). Wrapper = cash sitting uninvested inside ISA/SIPP/GIA.">
              <span className="text-emerald-400">{fmtGBP(summary.accessible_cash_gbp ?? 0)}</span>
              <span className="opacity-60"> accessible · </span>
              <span className={(summary.wrapper_cash_gbp ?? 0) > 10000 ? 'text-amber-400' : 'text-text-muted'}>
                {fmtGBP(summary.wrapper_cash_gbp ?? 0)}
              </span>
              <span className="opacity-60"> wrapper cash</span>
            </p>
          )}
          {summary?.most_recent_reprice_at && (() => {
            const severity = summary.prices_stale_severity ?? 'unknown'
            const dotCls = severity === 'fresh'
              ? 'bg-emerald-400 animate-pulse'
              : severity === 'stale'
              ? 'bg-amber-400'
              : severity === 'very_stale'
              ? 'bg-rose-500'
              : 'bg-text-muted'
            const labelCls = severity === 'very_stale'
              ? 'text-rose-400 font-semibold'
              : severity === 'stale'
              ? 'text-amber-400'
              : 'opacity-70'
            const prefix = severity === 'fresh'
              ? 'Live · updated'
              : severity === 'stale'
              ? 'Stale · last reprice'
              : severity === 'very_stale'
              ? 'PRICES STALE · last reprice'
              : 'Updated'
            const ageLabel = (() => {
              const diff = Date.now() - new Date(summary.most_recent_reprice_at!).getTime()
              const s = Math.floor(diff / 1000)
              if (s < 60) return `${s}s ago`
              const m = Math.floor(s / 60)
              if (m < 60) return `${m}m ago`
              const h = Math.floor(m / 60)
              if (h < 24) return `${h}h ago`
              const d = Math.floor(h / 24)
              return `${d}d ago`
            })()
            return (
              <p
                className="text-[11px] mt-0.5 leading-tight"
                title={`Last mark-to-market: ${summary.most_recent_reprice_at}\nSeverity: ${severity}\n${severity === 'very_stale' ? 'Run POST /api/accounts/refresh-prices or check if celery worker is running on Render.' : ''}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${dotCls}`} />
                <span className={labelCls}>{prefix} {ageLabel}</span>
              </p>
            )
          })()}
          {hasPriceCache && (
            <div className={`${positive ? 'stat-change-pos' : 'stat-change-neg'} mt-1`}>
              {positive ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}
              <span>
                {positive ? '+' : ''}{fmtGBP(liveChange)} ({fmtPct(liveChangePct, 2)}) today
              </span>
            </div>
          )}
          {navSeries.length >= 2 && (
            <div className="mt-2 flex items-end justify-between gap-2">
              <div className="h-10 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={navSeries}>
                    <defs>
                      <linearGradient id="nwSparkGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={navMonthUp ? '#10b981' : '#f43f5e'} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={navMonthUp ? '#10b981' : '#f43f5e'} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="nav" stroke={navMonthUp ? '#10b981' : '#f43f5e'} strokeWidth={1.5} fill="url(#nwSparkGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className={`shrink-0 ${navMonthUp ? 'delta-chip-pos' : 'delta-chip-neg'}`} title="30-day change">
                {navMonthUp ? '▲' : '▼'} {fmtPct(navMonthChange, 1)} · 30d
              </div>
            </div>
          )}
          {navSeriesLoaded && navSeries.length < 2 && (
            <p className="mt-2 text-[11px] text-text-muted">
              History starts today — first snapshot recorded
            </p>
          )}
        </div>

        {/* Daily P&L */}
        <div className="stat-card">
          <p className="stat-label cursor-help" title={JARGON_HELP.pnl}>Daily P&amp;L</p>
          {hasPriceCache ? (
            <>
              <p className={`stat-value ${positive ? 'text-emerald' : 'text-rose-light'}`}>
                {positive ? '+' : ''}{fmtGBP(liveChange)}
              </p>
              <p className="text-xs text-text-muted">{fmtPct(liveChangePct, 2)} today</p>
            </>
          ) : (
            <>
              <p className="stat-value text-text-muted">£0</p>
              <p className="text-xs text-text-muted">Live P&amp;L active when price cache enabled</p>
            </>
          )}
          {navSeries.length >= 2 && (
            <div className="mt-2 flex items-end justify-between gap-2">
              <div className="h-10 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={navSeries}>
                    <Bar dataKey="dailyPnl" radius={[1, 1, 0, 0]}>
                      {navSeries.map((r, i) => (
                        <Cell key={i} fill={(r.dailyPnl ?? 0) >= 0 ? '#10b981' : '#f43f5e'} fillOpacity={0.8} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className={`shrink-0 ${pnlMonthUp ? 'delta-chip-pos' : 'delta-chip-neg'}`} title="30-day cumulative P&L">
                {pnlMonthUp ? '▲' : '▼'} {fmtGBP(pnlMonthSum)} · 30d
              </div>
            </div>
          )}
          {navSeriesLoaded && navSeries.length < 2 && (
            <p className="mt-2 text-[11px] text-text-muted">
              History starts today — first snapshot recorded
            </p>
          )}
        </div>

        {/* Idle cash — money sitting uninvested; click through to Deploy Cash */}
        <Link to="/app/deploy-cash" className="stat-card block hover:border-border-light transition-colors">
          <p className="stat-label">Idle Cash</p>
          <p className={`stat-value ${(idleCash ?? 0) > 0 ? 'text-amber-400' : 'text-text-muted'}`}>
            {idleCash != null ? fmtGBP(idleCash) : '—'}
          </p>
          <p className="text-xs text-text-muted">
            {idleCash == null
              ? 'Checking your accounts…'
              : idleCash > 0
              ? 'Sitting uninvested — see a plan →'
              : 'Fully invested — nothing idle'}
          </p>
        </Link>

        {/* Next action — top £-impact insight from the Action Center engine */}
        <Link to="/app/actions" className="stat-card block hover:border-border-light transition-colors">
          <p className="stat-label">Next Action</p>
          {topInsight?.title ? (
            <>
              <p className="text-sm font-semibold text-text-primary leading-snug line-clamp-2">
                {topInsight.title}
              </p>
              <p className="text-xs text-text-muted mt-1">
                {topInsight.impact_gbp != null && topInsight.impact_gbp > 0
                  ? `Worth about £${Math.round(topInsight.impact_gbp).toLocaleString('en-GB')} — see all actions →`
                  : 'See all actions →'}
              </p>
            </>
          ) : (
            <>
              <p className="stat-value text-text-muted">—</p>
              <p className="text-xs text-text-muted">Nothing urgent right now</p>
            </>
          )}
        </Link>

        {/* Win Rate */}
        <div className="stat-card">
          <p className="stat-label cursor-help" title={JARGON_HELP.winRate}>Win Rate</p>
          <p className="stat-value">{winRate ?? '—'}</p>
          <p className="text-xs text-text-muted">
            {analysis
              ? (analysis.goals.overall_on_track ? 'Goals on track' : 'Review needed')
              : (analyzing ? 'Calculating…' : 'Run analysis to compute')}
          </p>
        </div>
      </div>
      )}

      {/* Fund Control — discretionary split */}
      {!prefs.fundControl ? (
        <ShowBar label={WIDGET_LABELS.fundControl} onShow={() => toggleWidget('fundControl')} />
      ) : !summary ? (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="panel-title">Fund Control</h2>
          </div>
          <EmptyState
            message="Fund split unavailable"
            sub={accountsLoading ? 'Loading household summary…' : 'Household summary could not be loaded'}
          />
        </div>
      ) : (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="panel-title">Fund Control</h2>
            <span className="text-xs text-text-muted font-mono cursor-help" title={JARGON_HELP.discretionary}>
              {fmtPct(summary.discretionary_pct, 1)} discretionary
            </span>
          </div>
          <div className="flex gap-2 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-emerald font-medium">Discretionary</span>
                <span className="text-text-primary font-mono">{fmtGBP(summary.discretionary_value)}</span>
              </div>
              <div className="h-2 bg-surface-hover rounded-full overflow-hidden" role="presentation">
                <div
                  className="h-full rounded-full bg-emerald transition-all"
                  style={{ width: `${summary.discretionary_pct}%` }}
                />
              </div>
              <p className="text-xs text-text-muted mt-1">{summary.discretionary_accounts} account{summary.discretionary_accounts !== 1 ? 's' : ''} — you can trade</p>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-text-muted font-medium">View Only</span>
                <span className="text-text-primary font-mono">{fmtGBP(summary.non_discretionary_value)}</span>
              </div>
              <div className="h-2 bg-surface-hover rounded-full overflow-hidden" role="presentation">
                <div
                  className="h-full rounded-full bg-text-muted transition-all"
                  style={{ width: `${100 - summary.discretionary_pct}%` }}
                />
              </div>
              <p className="text-xs text-text-muted mt-1">{summary.non_discretionary_accounts} account{summary.non_discretionary_accounts !== 1 ? 's' : ''} — managed/locked</p>
            </div>
          </div>
          <div className="h-2.5 bg-surface-hover rounded-full overflow-hidden flex" role="presentation" aria-label="Discretionary vs view-only split">
            <div className="h-full bg-emerald rounded-l-full transition-all" style={{ width: `${summary.discretionary_pct}%` }} />
            <div className="h-full bg-text-muted flex-1 rounded-r-full" />
          </div>
        </div>
      )}

      {/* Critical Alerts — top issues across the household */}
      <CriticalAlertsCard />

      {/* Smart Insights — proactive portfolio actions */}
      {!prefs.insights ? (
        <ShowBar label={WIDGET_LABELS.insights} onShow={() => toggleWidget('insights')} />
      ) : (
        <InsightsCard onTerminalCommand={cmd => navigateToTerminalCommand(navigate, cmd)} />
      )}

      {/* Proactive Insights — top 3-5 actionable insights from the engine */}
      {!prefs.proactiveInsights ? (
        <ShowBar label={WIDGET_LABELS.proactiveInsights} onShow={() => toggleWidget('proactiveInsights')} />
      ) : (
        <ProactiveInsightsWidget />
      )}

      {/* Intelligence Quick Links */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-9 gap-2">
        {([
          { to: '/app/daily?tab=pulse', label: 'Market Pulse', color: 'text-cyan-400', help: 'A one-page snapshot of what markets are doing right now' },
          { to: '/app/hidden-alpha', label: 'Hidden Alpha', color: 'text-amber-400', help: JARGON_HELP.alpha },
          { to: '/app/catalysts', label: 'Catalysts', color: 'text-emerald-400', help: JARGON_HELP.catalyst },
          { to: '/app/trade-ideas', label: 'Trade Ideas', color: 'text-amber-400' },
          { to: '/app/risk-radar', label: 'Risk Radar', color: 'text-rose-400' },
          { to: '/app/signals', label: 'Signals', color: 'text-purple-400' },
          { to: '/app/smart-money', label: 'Smart Money', color: 'text-blue-400', help: JARGON_HELP.smartMoney },
          { to: '/app/phd-swarm', label: 'PhD Swarm', color: 'text-indigo-400', help: JARGON_HELP.phdSwarm },
          { to: '/app/signal-quality', label: 'Signal Quality', color: 'text-emerald-400', help: JARGON_HELP.signalQuality },
        ] as { to: string; label: string; color: string; help?: string }[]).map(item => (
          <Link key={item.to} to={item.to}
            title={item.help}
            className={clsx('flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-surface-card hover:bg-surface-hover text-xs font-semibold transition-colors', item.help && 'cursor-help')}>
            <span className={item.color}>{item.label}</span>
          </Link>
        ))}
      </div>

      {/* Main content grid — chart (2 cols) + Market Intelligence */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Portfolio chart (2 cols) */}
        {!prefs.chart ? (
          <div className="lg:col-span-2">
            <ShowBar label={WIDGET_LABELS.chart} onShow={() => toggleWidget('chart')} />
          </div>
        ) : (
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="panel-title">
                {hasHistory ? 'Fund Performance (30 days)' : 'Portfolio Snapshot — Account Balances'}
              </h2>
              {!hasHistory && (
                <p className="text-xs text-text-muted mt-0.5 flex items-center gap-1">
                  <AlertCircle size={11} aria-hidden />
                  Performance tracking starts when Celery price history is enabled
                </p>
              )}
            </div>
            <Link to="/app/portfolio" className="text-xs text-blue-light hover:underline flex items-center gap-1">
              Full view <ArrowRight size={12} />
            </Link>
          </div>

          <DataSection
            loading={portfolioLoading && !hasHistory && accountBarData.length === 0}
            error={portfolioError ?? (accountsError && !accountBarData.length ? accountsError : null)}
            empty={!portfolioLoading && !accountsLoading && !hasHistory && accountBarData.length === 0}
            emptyMessage="Add accounts in Settings → Accounts to see your portfolio here"
            onRetry={() => { fetchSnapshot(); fetchHistory(30); fetchAccounts() }}
            skeletonRows={5}
          >
            {hasHistory ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="blueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} interval={6} />
                  <YAxis
                    tickFormatter={v => `£${(v / 1e6).toFixed(2)}M`}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={72}
                  />
                  <Tooltip
                    formatter={(v: number) => [fmtGBP(v), 'Value']}
                    contentStyle={{ background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', borderRadius: 8, color: 'rgb(var(--text-primary))' }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="url(#blueGrad)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <AccountSnapshot accounts={accountBarData} />
            )}
          </DataSection>
        </div>
        )}

        {/* ARIA insight panel — Market Intelligence */}
        {!prefs.intelligence ? (
          <ShowBar label={WIDGET_LABELS.intelligence} onShow={() => toggleWidget('intelligence')} />
        ) : (
        <div className="card p-5 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple/20 border border-purple/30 flex items-center justify-center">
              <Bot size={16} className="text-purple-light" aria-hidden />
            </div>
            <h2 className="panel-title">Market Intelligence</h2>
          </div>

          <DataSection
            loading={analyzing && !analysis}
            empty={!analyzing && !analysis}
            emptyMessage="Run /analyze in terminal or click Refresh to trigger agent analysis"
            onRetry={() => runAnalysis()}
            skeletonRows={4}
          >
            <>
              <p className="text-sm text-text-secondary leading-relaxed">{analysis?.aria_summary}</p>
              <div className="space-y-3 pt-2 border-t border-border">
                <ScoreBar label="Stress level" value={analysis?.behavioral.stress_level ?? 0} />
                <ScoreBar label="Overconfidence" value={analysis?.behavioral.overconfidence ?? 0} />
                <ScoreBar label="Tech affinity" value={analysis?.behavioral.tech_affinity ?? 0} />
              </div>
              <span
                className={`badge self-start ${
                  analysis?.behavioral.sentiment === 'bullish'
                    ? 'badge-green'
                    : analysis?.behavioral.sentiment === 'anxious'
                      ? 'badge-red'
                      : 'badge-blue'
                }`}
              >
                {analysis?.behavioral.sentiment}
              </span>
              <Link
                to="/app/indices"
                className="text-xs text-blue-light hover:underline flex items-center gap-1 self-start"
              >
                View live market indices <ArrowRight size={11} />
              </Link>
            </>
          </DataSection>
        </div>
        )}
      </div>

      {/* Allocation — asset class / wrapper / owner donut */}
      {!prefs.allocation ? (
        <ShowBar label={WIDGET_LABELS.allocation} onShow={() => toggleWidget('allocation')} />
      ) : summary ? (
        <AllocationWidget summary={summary} />
      ) : (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 size={16} className="text-blue-light" aria-hidden />
            <h2 className="panel-title">Allocation</h2>
          </div>
          <EmptyState
            icon={<BarChart2 size={24} aria-hidden />}
            message="Allocation unavailable"
            sub={accountsLoading ? 'Loading household summary…' : 'Household summary could not be loaded'}
          />
        </div>
      )}

      {/* Bottom row — Strategy Desk + Risk Desk */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Action Plan — personalised rules-based recommendations */}
        {!prefs.strategyDesk ? (
          <div className="lg:col-span-2">
            <ShowBar label={WIDGET_LABELS.strategyDesk} onShow={() => toggleWidget('strategyDesk')} />
          </div>
        ) : (
        <ActionPlanPanel />
        )}

        {/* Risk Desk */}
        {!prefs.riskDesk ? (
          <ShowBar label={WIDGET_LABELS.riskDesk} onShow={() => toggleWidget('riskDesk')} />
        ) : (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target size={16} className="text-amber" aria-hidden />
            <h2 className="panel-title">Risk Desk</h2>
          </div>

          <DataSection
            loading={accountsLoading && !summary && !analysis}
            empty={!accountsLoading && !analyzing && !summary && !analysis}
            emptyMessage="Run analysis to compute risk metrics"
            onRetry={() => { fetchSummary(); runAnalysis() }}
            skeletonRows={3}
          >
            {analysis ? (
              <>
                <ul className="space-y-3" aria-label="Risk desk pot rules">
                  {[
                    {
                      label: 'Concentration risk',
                      // overall_risk_score is 0–10 (see backend schemas) — normalise to 0–1 for the bar.
                      value: (analysis.risk.overall_risk_score ?? 0) / 10,
                      color:
                        (analysis.risk.overall_risk_score ?? 0) > 6
                          ? '#f43f5e'
                          : (analysis.risk.overall_risk_score ?? 0) > 3.5
                            ? '#f59e0b'
                            : '#10b981',
                      note: analysis.risk.risk_label,
                    },
                    {
                      label: 'Discretionary exposure',
                      value: summary ? summary.discretionary_pct / 100 : 0,
                      color: '#3b82f6',
                      note: summary ? `${fmtGBP(summary.discretionary_value)} tradeable` : '—',
                    },
                    {
                      label: 'Non-discretionary / locked',
                      value: summary ? (100 - summary.discretionary_pct) / 100 : 0,
                      color: '#94a3b8',
                      note: summary ? `${fmtGBP(summary.non_discretionary_value)} view-only` : '—',
                    },
                  ].map(s => (
                    <li key={s.label} className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} aria-hidden />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between text-xs text-text-secondary mb-1">
                          <span>{s.label}</span>
                          <span className="font-mono text-text-muted">{s.note}</span>
                        </div>
                        <div className="h-1.5 bg-surface-hover rounded-full">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${Math.min(s.value * 100, 100)}%`, backgroundColor: s.color }}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex items-center gap-2">
                  <Shield size={13} className="text-text-muted" aria-hidden />
                  <p className="text-xs text-text-muted cursor-help" title={JARGON_HELP.var}>Worst likely day (95%): {fmtGBP(analysis.risk.var_95_daily)}</p>
                </div>
              </>
            ) : summary ? (
              <>
                <ul className="space-y-3" aria-label="Portfolio pot split">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full shrink-0 bg-emerald" aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs text-text-secondary mb-1">
                        <span>Discretionary — you can trade</span>
                        <span className="font-mono">{fmtGBP(summary.discretionary_value)}</span>
                      </div>
                      <div className="h-1.5 bg-surface-hover rounded-full">
                        <div className="h-full rounded-full bg-emerald transition-all" style={{ width: `${summary.discretionary_pct}%` }} />
                      </div>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full shrink-0 bg-text-muted" aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs text-text-secondary mb-1">
                        <span>Managed / locked — view only</span>
                        <span className="font-mono">{fmtGBP(summary.non_discretionary_value)}</span>
                      </div>
                      <div className="h-1.5 bg-surface-hover rounded-full">
                        <div
                          className="h-full rounded-full bg-text-muted transition-all"
                          style={{ width: `${100 - summary.discretionary_pct}%` }}
                        />
                      </div>
                    </div>
                  </li>
                </ul>
                <div className="mt-4 flex items-center gap-2">
                  <Shield size={13} className="text-text-muted" aria-hidden />
                  <p className="text-xs text-text-muted cursor-help" title={JARGON_HELP.var}>Worst likely day (95%): — · Run analysis to compute</p>
                </div>
              </>
            ) : (
              <span />
            )}
          </DataSection>
        </div>
        )}
      </div>

      {/* Portfolio Top Movers */}
      {!prefs.topMovers ? (
        <ShowBar label={WIDGET_LABELS.topMovers} onShow={() => toggleWidget('topMovers')} />
      ) : (
        <TopMoversCard />
      )}

      {/* Upcoming Dividends */}
      {!prefs.dividends ? (
        <ShowBar label={WIDGET_LABELS.dividends} onShow={() => toggleWidget('dividends')} />
      ) : (
        <UpcomingDividendsCard />
      )}

      {/* Top Signals + Convergences */}
      <div className="grid md:grid-cols-2 gap-6">
        {!prefs.topSignals ? (
          <ShowBar label={WIDGET_LABELS.topSignals} onShow={() => toggleWidget('topSignals')} />
        ) : (
          <TopSignalsWidget />
        )}

        {!prefs.convergences ? (
          <ShowBar label={WIDGET_LABELS.convergences} onShow={() => toggleWidget('convergences')} />
        ) : (
          <ConvergencesWidget />
        )}
      </div>

      {/* Multi-Category Convergence — highest-quality setup surface */}
      <MultiCategoryWidget />

      {/* Perfect Storms — every time all filters aligned simultaneously */}
      <PerfectStormWidget />

      {/* Signal Velocity — hourly production over 24h */}
      <SignalVelocityWidget />

      {/* Recent Alerts — unified feed across alert streams */}
      <RecentAlertsWidget />

      {/* Conviction distribution — pipeline depth histogram */}
      <ConvictionHistogramWidget />

      {/* Top Heat — hottest tickers right now */}
      <TopHeatWidget />

      {/* Sector Dispersion — rotation-regime detector + pairs idea generator */}
      <SectorDispersionWidget />

      {/* Holdings Heatmap — sentiment colour-wash across your book */}
      <HoldingsHeatmap />

      {/* Holdings Health Ranking — worst-first action list */}
      <HoldingsHealthWidget />

      {/* Trade Ideas + Risk Radar */}
      <div className="grid md:grid-cols-2 gap-6">
        {!prefs.tradeIdeas ? (
          <ShowBar label={WIDGET_LABELS.tradeIdeas} onShow={() => toggleWidget('tradeIdeas')} />
        ) : (
          <TradeIdeasWidget />
        )}
        {!prefs.riskRadar ? (
          <ShowBar label={WIDGET_LABELS.riskRadar} onShow={() => toggleWidget('riskRadar')} />
        ) : (
          <RiskRadarWidget />
        )}
      </div>

      {/* Live P&L + Smart Advisor + Activity Feed */}
      {!prefs.livePnl ? (
        <ShowBar label={WIDGET_LABELS.livePnl} onShow={() => toggleWidget('livePnl')} />
      ) : (
      <div className="grid lg:grid-cols-3 gap-6">
        <LivePnlCard />
        <SmartAdvisorPanel />
        <ActivityFeed maxItems={6} />
      </div>
      )}

      {/* Recently Viewed strip */}
      <RecentlyViewedStrip />

      {/* Quick Trade FAB now lives in Shell — see SplitFAB */}
    </div>
  )
}
